<template>
  <main id="create-quote-page" class="page-container">
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <VueFinalModal
      v-model="showDuplicateModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content dup-modal__content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="closeDuplicateModal">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <h3 class="modal__title">Preventivo già presente</h3>

        <p class="dup-modal__intro">
          Esiste già un preventivo per questo cliente allo stesso indirizzo in
          corso...
          <span
            v-if="lastDuplicateQuoteInfo?.address"
            class="dup-modal__address"
          >
            ({{ lastDuplicateQuoteInfo.address }}) </span
          >.
        </p>

        <!-- loading -->
        <div v-if="dupLoading" class="dup-modal__skeleton">
          Caricamento in corso…
        </div>
        <!-- error -->
        <div v-else-if="dupError" class="dup-modal__error">{{ dupError }}</div>

        <!-- dati -->
        <div v-else-if="dupQuote" class="dup-quote">
          <div class="dup-quote__grid">
            <div>
              <div class="dup-quote__label">ID</div>
              <div class="dup-quote__value">#{{ dupQuote.id }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Data</div>
              <div class="dup-quote__value">
                {{ formatToLocalDate(dupQuote.date) }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Cliente</div>
              <div class="dup-quote__value">
                {{ dupQuote.clientName || "—" }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Pagamento</div>
              <div class="dup-quote__value">{{ dupQuote.payment_method }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Indirizzo</div>
              <div class="dup-quote__value">
                <a
                  v-if="linkMaps(dupQuote.location)"
                  :href="linkMaps(dupQuote.location)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                >
                  {{ dupQuote.location?.address || "Vedi mappa" }}
                </a>
                <span v-else>{{ dupQuote.location?.address || "—" }}</span>
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Totale</div>
              <div class="dup-quote__value">
                {{ money(dupQuote.totalPrice) }}
              </div>
            </div>
          </div>

          <div class="dup-quote__items">
            <div class="dup-quote__items-head">
              <strong>Riepilogo articoli</strong>
              <span class="muted">({{ dupQuote.items?.length || 0 }})</span>
            </div>
            <ul class="dup-quote__items-list">
              <li
                v-for="(it, idx) in dupQuote.items || []"
                :key="idx"
                class="dup-item"
              >
                <div class="dup-item__name">
                  {{ it.itemName || it.brand || "Articolo" }}
                </div>
                <div class="dup-item__meta">
                  <span v-if="it.category" class="chip">{{ it.category }}</span>
                  <span v-if="it.boxQuantity"
                    >scatole: {{ it.boxQuantity }}</span
                  >
                  <span v-if="it.pieces">pezzi: {{ it.pieces }}</span>
                  <span v-if="it.iva !== undefined"
                    >IVA: {{ etichettaIva(it) }}</span
                  >
                  <span v-if="it.price !== undefined"
                    >€ {{ number(it.price) }}</span
                  >
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="dup-modal__footer">
          <button
            v-if="dupQuote && !dupLoading && dupDocuments.length"
            class="btn-primary"
            @click="showDupDocs = true"
          >
            Apri file
          </button>

          <button class="btn-primary" @click="onChangeAddress">
            Usa indirizzo diverso
          </button>

          <button
            class="btn-ghost"
            :disabled="!lastDuplicateQuoteInfo?.quoteId"
            @click="onDuplicateWithEdit"
          >
            Duplica modificando indirizzo
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modale documenti PDF del preventivo duplicato -->
    <VueFinalModal
      v-model="showDupDocs"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="documents-modal-content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="showDupDocs = false">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Preventivo #{{ dupQuote?.id }}</h3>

        <div class="documents-grid">
          <div v-for="doc in dupDocuments" :key="doc.id" class="document-card">
            <a :href="doc.url" target="_blank" class="card-link">
              <div class="card-icon-pdf">
                <i class="fa-solid fa-file-pdf"></i>
              </div>
              <div class="card-body">
                <div class="card-title">{{ formatDocName(doc.name) }}</div>
                <div class="card-subtitle">
                  Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                </div>
              </div>
            </a>
          </div>
          <div v-if="dupDocuments.length === 0" class="no-result">
            Nessun documento trovato
          </div>
        </div>
      </template>
    </VueFinalModal>

    <div class="create-quote-wrapper">
      <div class="create-quote-main-col" ref="mainCol">
        <!-- Form principale -->
        <form
          class="create-quote-form"
          :class="{ 'form--readonly': readOnly }"
          @submit.prevent="readOnly ? undefined : onUpdateQuoteAndGeneratePdf"
        >
          <!-- SEZIONE: Anagrafica preventivo -->
          <section class="quote-section">
            <h3 class="quote-section__title">
              <i class="fa-solid fa-file-lines"></i> Anagrafica preventivo
            </h3>
            <div class="quote-section__grid">
              <!-- Colonna 1: Cliente + Modalità di pagamento -->
              <div class="quote-section__col">
                <!-- Seleziona Cliente -->
                <div class="field" id="client-select">
                  <label>Cliente</label>

                  <SearchableCombo
                    v-model="clientIdModel"
                    v-model:text="searchQueryClient"
                    :items="filteredClients"
                    :labelKey="getClientLabel"
                    :valueKey="(c) => String(c?.id)"
                    placeholder="Cerca cliente…"
                    :loading="false"
                    :disabled="readOnly"
                    @search="
                      (q) => {
                        searchQueryClient = q || '';
                      }
                    "
                    @select="
                      (c) => {
                        formData.clientId = c?.id ?? '';
                      }
                    "
                  />
                </div>

                <!-- Modalità di pagamento -->
                <div class="field">
                  <label for="payment-method">Modalità di pagamento</label>
                  <StatusSelect
                    v-model="formData.payment_method"
                    :options="paymentOptions"
                    placeholder="Seleziona metodo..."
                    placeholderIcon="fa-wallet"
                    :disabled="readOnly"
                  />
                </div>
              </div>

              <!-- Colonna 2: Data + Indirizzo -->
              <div class="quote-section__col">
                <!-- Data Preventivo -->
                <div class="field">
                  <label for="date">Data</label>
                  <FlatPickr
                    id="date"
                    v-model="formData.date"
                    :config="dateConfig"
                    placeholder="gg/mm/aaaa"
                    :disabled="readOnly"
                  />
                </div>

                <!-- Indirizzo: sempre visibile; readonly + nota "Ritiro in sede" quando non è consegna -->
                <div class="field">
                  <label for="address">Indirizzo di consegna</label>
                  <input
                    type="text"
                    id="address"
                    v-model="addressModel"
                    :readonly="readOnly || formData.deliveryType !== 'consegna'"
                    :class="{
                      'readonly-field':
                        readOnly || formData.deliveryType !== 'consegna',
                    }"
                    placeholder="Inserisci indirizzo completo…"
                    autocomplete="off"
                    @mousedown="readOnly ? $event.preventDefault() : undefined"
                  />
                </div>
              </div>

              <!-- Colonna 3: Modalità di consegna -->
              <div class="quote-section__col">
                <div class="field">
                  <label>
                    Modalità di consegna <span class="required-mark">*</span>
                  </label>
                  <StatusSelect
                    v-model="deliveryTypeModel"
                    :options="deliveryOptions"
                    placeholder="Seleziona modalità..."
                    placeholderIcon="fa-truck"
                    :disabled="readOnly"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- RIGA: form articolo (40%) + articoli aggiunti (60%) -->
          <div
            class="quote-work-row"
            :class="{ 'quote-work-row--full': readOnly }"
          >
            <!-- Colonna sinistra (40%): form articolo + note e addebiti -->
            <div class="quote-work-col">
              <!-- SEZIONE: Articolo (form) -->
              <section v-if="!readOnly" class="quote-section">
                <h3 class="quote-section__title">
                  <i class="fa-solid fa-cart-shopping"></i> Articolo
                </h3>
                <div class="quote-section__col">
                  <div class="create-quote-row row-3">
                    <!-- scegli Articolo -->
                    <div class="field" id="item-select">
                      <label>Articolo</label>

                      <SearchableCombo
                        ref="itemCombo"
                        v-model="newItem.itemId"
                        v-model:text="searchQueryItem"
                        :items="filteredItems"
                        :labelKey="itemSearchLabel"
                        :valueKey="(it) => it?.id"
                        placeholder="Cerca articolo…"
                        :disabled="false"
                        :loading="itemsLoading"
                        :current-page="itemsPage"
                        :requested-page="itemsRequestedPage"
                        :total-pages="itemsTotalPages"
                        :total-items="itemsTotalItems"
                        :show-pagination="true"
                        @search="(q) => debouncedFetchItems((q || '').trim())"
                        @select="selectItem"
                        @page-change="onItemPageChange"
                        @cleared="clearItemSelection"
                      >
                        <template #option="{ option }">
                          <span class="quote-item-option">
                            <span class="quote-item-option__content">
                              <span
                                class="quote-item-option__label"
                                :class="{
                                  'is-out': isOrderableOutOfStock(option),
                                }"
                              >
                                <span v-if="isOrderableOutOfStock(option)"
                                  >! </span
                                >{{ itemSearchLabel(option) }}
                              </span>
                              <span
                                v-if="itemFormatFinish(option)"
                                class="quote-item-option__meta"
                              >
                                {{ itemFormatFinish(option) }}
                              </span>
                            </span>
                            <span
                              class="quote-item-option__stock"
                              :class="{
                                'in-stock': warehouseCount(option) > 0,
                              }"
                              :title="stockTitle(option)"
                            >
                              {{ stockLabel(option) }}
                            </span>
                          </span>
                        </template>
                      </SearchableCombo>
                      <p class="create-item-cta">
                        Articolo non trovato?
                        <button
                          type="button"
                          class="create-item-link"
                          @click.prevent.stop="openCreateItemModal"
                        >
                          <i class="fa-solid fa-circle-plus"></i> Crea articolo
                        </button>
                      </p>
                    </div>

                    <!-- Quantità a pezzi: solo per articoli non venduti a scatola -->
                    <div class="field" v-if="!showMqField">
                      <label for="quantity">{{ quantityLabel }}</label>
                      <input
                        id="quantity"
                        step="1"
                        min="1"
                        type="number"
                        v-model="quantityInput"
                        placeholder="Inserisci"
                        :disabled="readOnly || !articoloScelto"
                      />
                    </div>

                    <!-- Articoli a mq/ml: si scrivono i mq e basta. Le scatole intere
                         necessarie sono derivate dietro le quinte e mostrate nell'hint. -->
                    <div class="field" v-else>
                      <label for="mq-per-box">{{ mqFieldLabel }}</label>
                      <input
                        id="mq-per-box"
                        type="number"
                        min="0"
                        step="any"
                        v-model="mqInput"
                        :placeholder="mqFieldPlaceholder"
                        :disabled="readOnly || !articoloScelto"
                      />
                      <!-- Dati scatola incompleti: l'articolo si può comunque
                           preventivare, ma la scheda va completata prima di
                           confermare il preventivo al cliente. -->
                      <small
                        v-if="missingBoxDataInfo"
                        class="field-warning"
                        role="alert"
                      >
                        {{ missingBoxDataInfo.text }}
                        <router-link
                          :to="missingBoxDataInfo.route"
                          target="_blank"
                          class="field-warning-link"
                          >Completa la scheda articolo</router-link
                        >
                      </small>
                      <small v-if="mqSummary" class="field-hint">{{
                        mqSummary
                      }}</small>
                    </div>
                  </div>

                  <!-- Flusso prezzo (IVA esclusa): listino → scontato → IVA -->
                  <div
                    v-if="!readOnly"
                    class="create-quote-row row-4 price-flow"
                  >
                    <!-- 1) Prezzo di listino NETTO (IVA esclusa), readonly -->
                    <div class="field">
                      <label for="price">Prezzo listino — IVA esclusa</label>
                      <input
                        id="price"
                        type="number"
                        v-model="newItem.price"
                        placeholder="Seleziona l'articolo per visualizzare il prezzo corrispondente"
                        step="0.01"
                        readonly
                        class="readonly-field"
                      />
                    </div>
                    <!-- 2) Prezzo scontato NETTO (opzionale): prezzo netto da
                 applicare, digitato dall'operatore. Vuoto → usa il listino. -->
                    <div class="field">
                      <label for="discounted-price">Prezzo scontato</label>
                      <input
                        id="discounted-price"
                        type="number"
                        v-model="newItem.discountedPrice"
                        placeholder="Prezzo netto scontato (opzionale)"
                        step="0.01"
                        min="0"
                        :disabled="readOnly || !quantitaInserita"
                      />
                    </div>
                    <!-- 3) IVA applicata alla riga (default 0%) -->
                    <div class="field">
                      <label for="vat-rate">IVA</label>
                      <StatusSelect
                        v-model="newItem.iva"
                        :options="ivaOptions"
                        placeholderIcon="fa-percent"
                        :disabled="readOnly || !articoloScelto"
                      />
                    </div>
                  </div>

                  <!-- [sc-49176] Senza IVA: la Natura dell'operazione e il
                       riferimento normativo che la giustifica. Senza Natura
                       la riga non si aggiunge: SdI scarterebbe la fattura. -->
                  <div
                    v-if="Number(newItem.iva) === 0"
                    class="create-quote-row"
                  >
                    <div class="field">
                      <!-- StatusSelect non prende un id per il suo pulsante: la
                           label resta senza «for». -->
                      <label>Natura</label>
                      <StatusSelect
                        v-model="newItem.natura"
                        :options="naturaOptions"
                        placeholder="Scegli la Natura"
                        :disabled="readOnly || !articoloScelto"
                      />
                      <small
                        v-if="!newItem.natura"
                        class="field-hint field-hint--errore"
                        >Senza IVA serve la Natura dell'operazione.</small
                      >
                    </div>
                    <div class="field">
                      <label for="legal-reference">Riferimento normativo</label>
                      <!-- [sc-49176] Non si scrive: è quello della Natura. -->
                      <input
                        id="legal-reference"
                        type="text"
                        :value="riferimentoPredefinito(newItem.natura) || ''"
                        placeholder="Si riempie scegliendo la Natura"
                        readonly
                        class="readonly-field"
                      />
                    </div>
                  </div>

                  <!-- Anteprima riga: imponibile → imposta → totale (IVA esclusa
                       → ivato). Solo preview, non input. -->
                  <div v-if="!readOnly" class="create-quote-row line-preview">
                    <span class="line-preview__item"
                      >Imponibile:
                      <strong>€ {{ previewFormatted.taxable }}</strong></span
                    >
                    <span class="line-preview__item"
                      >Aliquota:
                      <strong>{{ etichettaIva(newItem) }}</strong></span
                    >
                    <span class="line-preview__item"
                      >Imposta:
                      <strong>€ {{ previewFormatted.tax }}</strong></span
                    >
                    <span class="line-preview__item line-preview__total"
                      >Totale:
                      <strong>€ {{ previewFormatted.gross }}</strong></span
                    >
                  </div>

                  <!-- RIGA 5: bottone "+ aggiungi articolo" + "Mostra articoli" -->
                  <div v-if="!readOnly" class="create-quote-row row-5">
                    <!-- Bottone per aggiungere un ulteriore alla lista -->
                    <div class="field">
                      <button
                        type="button"
                        class="add-item-btn"
                        :disabled="!canAddItem"
                        @click="addOrUpdateItem"
                      >
                        <!-- icona -->
                        <i class="fa-solid fa-plus icon" aria-hidden="true"></i>
                        <span>aggiungi articolo</span>
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- SEZIONE: Note e addebiti (sotto il form articolo) -->
              <section class="quote-section">
                <h3 class="quote-section__title">
                  <i class="fa-solid fa-note-sticky"></i> Note e addebiti
                </h3>
                <!-- RIGA 6: note -->
                <div class="create-quote-row row-5 notes-row">
                  <div class="field">
                    <label for="technical-requests">Note</label>
                    <textarea
                      id="technical-requests"
                      v-model.trim="formData.technical_queries"
                      placeholder="Inserisci note"
                      rows="3"
                      :readonly="readOnly"
                      @mousedown="
                        readOnly ? $event.preventDefault() : undefined
                      "
                    ></textarea>
                  </div>
                </div>

                <!-- RIGA 7: Addebiti -->
                <div class="create-quote-row addebiti-row">
                  <div class="field">
                    <label>Addebiti</label>
                    <hr />
                    <div class="addebiti-line">
                      <span class="addebiti-label">Spesa imballo (€30,00)</span>
                      <label
                        class="addebiti-option"
                        :style="
                          readOnly
                            ? { cursor: 'default', pointerEvents: 'none' }
                            : {}
                        "
                      >
                        <input
                          type="radio"
                          :value="false"
                          v-model="formData.hasPackagingFee"
                          :disabled="readOnly"
                        />
                        No
                      </label>
                      <label
                        class="addebiti-option"
                        :style="
                          readOnly
                            ? { cursor: 'default', pointerEvents: 'none' }
                            : {}
                        "
                      >
                        <input
                          type="radio"
                          :value="true"
                          v-model="formData.hasPackagingFee"
                          :disabled="readOnly"
                        />
                        Sì
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <!-- /.quote-work-col -->

            <!-- Colonna destra (60%): articoli aggiunti + salva -->
            <div class="quote-work-col">
              <!-- Articoli aggiunti -->
              <aside class="items-panel" ref="itemsPanel">
                <div class="items-panel__title-bar">
                  <h3 class="items-panel__title">Articoli aggiunti</h3>
                </div>
                <div class="items-panel__table-wrap">
                  <table class="items-panel__table">
                    <thead>
                      <tr>
                        <th>Codice</th>
                        <th>Articolo</th>
                        <th>Categoria</th>
                        <th>Quantità</th>
                        <th>Prezzo</th>
                        <th>IVA</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in formData.items"
                        :key="index"
                        :class="{
                          'quote-item-row--incomplete':
                            isRowStockIncomplete(item),
                        }"
                        :title="rowStockWarning(item)"
                      >
                        <td class="item-code-cell">
                          {{
                            getItemFromStore(
                              item.itemId,
                              categoryHintForRow(item)
                            ).supplierCode ||
                            getItemFromStore(
                              item.itemId,
                              categoryHintForRow(item)
                            ).itemCode ||
                            "—"
                          }}
                        </td>
                        <td>
                          <div class="item-title">
                            {{
                              displayForId(
                                item.itemId,
                                categoryHintForRow(item)
                              ).title
                            }}
                          </div>
                          <div
                            v-if="
                              displayForId(
                                item.itemId,
                                categoryHintForRow(item)
                              ).meta
                            "
                            class="item-meta"
                          >
                            {{
                              displayForId(
                                item.itemId,
                                categoryHintForRow(item)
                              ).meta
                            }}
                          </div>
                        </td>
                        <td>
                          {{
                            getItemFromStore(
                              item.itemId,
                              categoryHintForRow(item)
                            ).category
                          }}
                        </td>
                        <td>
                          <input
                            type="number"
                            class="panel-qty-input"
                            :value="rowQtyValue(item)"
                            :step="rowQtyStep(item)"
                            :title="rowQtyTitle(item)"
                            min="0"
                            :disabled="readOnly"
                            @change="
                              readOnly
                                ? undefined
                                : updateRowQty(
                                    item,
                                    +$event.target.value,
                                    $event.target
                                  )
                            "
                          />
                          <span class="panel-qty-unit">{{
                            rowQtyUnit(item)
                          }}</span>
                          <!-- Si fattura la scatola intera: la quantità che il
                               cliente aveva chiesto resta visibile. -->
                          <span
                            v-if="rowRequestedNote(item)"
                            class="panel-qty-requested"
                            >{{ rowRequestedNote(item) }}</span
                          >
                          <!-- Disponibilità mostrata solo se le scatole sono
                               calcolabili: senza mq/ml per scatola i numeri
                               sarebbero inventati. -->
                          <template
                            v-if="
                              rowHasBoxData(item) &&
                              isOrderableOutOfStock(
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                )
                              )
                            "
                          >
                            <span class="item-order-queued-badge"
                              >Esaurito</span
                            >
                            <span class="item-order-info-msg"
                              >Necessario ordinarlo per la quantità
                              richiesta.</span
                            >
                          </template>
                          <template
                            v-else-if="
                              rowHasBoxData(item) && isQtyExceedingStock(item)
                            "
                          >
                            <span
                              class="item-order-queued-badge item-not-available-badge"
                              >Non disponibile</span
                            >
                            <span class="item-order-info-msg"
                              >Necessario ordinarne
                              {{ qtyToOrderLabel(item) }} per la quantità
                              richiesta.</span
                            >
                          </template>
                        </td>
                        <td>
                          {{ formatPrice(item.price) }}
                          {{ unitForRow(item) }}
                        </td>
                        <td>
                          <!-- [sc-49176] Riga a 0% senza Natura (storica): la
                               si sceglie qui, o non si salva. Su un ordine
                               chiuso (task 11) la tendina resta finché la
                               scelta non è salvata con «Salva la Natura». -->
                          <StatusSelect
                            v-if="
                              !readOnly &&
                              (rigaSenzaNatura(item) || naturaInSospeso(item))
                            "
                            class="natura-in-riga"
                            :modelValue="item.natura"
                            :options="naturaOptions"
                            placeholder="Scegli la Natura"
                            @update:modelValue="scegliNatura(item, $event)"
                          />
                          <template v-else>{{ etichettaIva(item) }}</template>
                        </td>
                        <td>
                          <button
                            v-if="!readOnly"
                            type="button"
                            class="icons-button"
                            id="remove-item-button"
                            @click="removeItem(index)"
                            title="Rimuovi articolo dalla lista"
                            aria-label="Rimuovi articolo dalla lista"
                          >
                            <i class="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="formData.items.length === 0">
                        <td colspan="7" class="no-result">
                          Nessun articolo presente
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="items-panel__footer">
                  {{ formData.items.length }}
                  {{ formData.items.length === 1 ? "articolo" : "articoli" }}
                </div>
              </aside>

              <!-- Salva/Aggiorna: sotto il riepilogo articoli aggiunti -->
              <div class="save-button-container">
                <template v-if="readOnly">
                  <!-- [sc-48692] Pagato e in consegna: niente più modifiche. -->
                  <!-- [sc-48351 / task 48786] «Modifica» a chi il ruolo
                       permette di modificare questo documento. -->
                  <p
                    v-if="can(areaDocumento, 'M') && modificaChiusa"
                    class="modifica-chiusa"
                  >
                    Ordine pagato e con la consegna iniziata: non si modifica
                    più.
                  </p>
                  <router-link
                    v-else-if="can(areaDocumento, 'M')"
                    :to="
                      isOrderRoute
                        ? `/home/ordini/modifica/${quoteId}`
                        : `/home/preventivi/modifica/${quoteId}`
                    "
                    class="primary-btn"
                    :title="
                      isOrderRoute ? 'Modifica ordine' : 'Modifica preventivo'
                    "
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                    Modifica
                  </router-link>
                  <!-- [sc-49176 / task 11] Righe storiche senza Natura su un
                       ordine chiuso: la fattura non parte finché non la si
                       sceglie, e la si sceglie dalla pagina di modifica. -->
                  <router-link
                    v-if="
                      can(areaDocumento, 'M') &&
                      modificaChiusa &&
                      righeSenzaNatura
                    "
                    :to="
                      isOrderRoute
                        ? `/home/ordini/modifica/${quoteId}`
                        : `/home/preventivi/modifica/${quoteId}`
                    "
                    class="btn-secondary"
                    title="Scegli la Natura delle righe senza IVA"
                  >
                    Scegli la Natura delle righe senza IVA
                  </router-link>
                </template>
                <template v-else>
                  <p v-if="modificaChiusa" class="modifica-chiusa">
                    Ordine pagato e con la consegna iniziata: non si modifica
                    più.
                  </p>
                  <!-- [sc-49176] Righe senza IVA e senza Natura: si dice perché il
                       salvataggio è spento. -->
                  <!-- [sc-49176 / task 11] Su un ordine chiuso si salva la sola
                       Natura, col suo pulsante. -->
                  <p
                    v-if="
                      modificaChiusa && (righeSenzaNatura || naturaDaSalvare)
                    "
                    class="natura-mancante"
                  >
                    Ci sono righe senza IVA e senza Natura: sceglila nella
                    colonna IVA e premi «Salva la Natura».
                  </p>
                  <p v-else-if="righeSenzaNatura" class="natura-mancante">
                    Ci sono righe senza IVA e senza Natura: sceglila nella
                    colonna IVA per salvare.
                  </p>
                  <button
                    v-if="
                      modificaChiusa &&
                      can(areaDocumento, 'M') &&
                      (righeSenzaNatura || naturaDaSalvare)
                    "
                    type="button"
                    id="save-natura"
                    class="btn-secondary"
                    :disabled="!naturaDaSalvare || salvandoNatura"
                    @click="salvaNatura"
                    :title="
                      !naturaDaSalvare
                        ? 'Scegli la Natura nella colonna IVA'
                        : 'Salva la Natura delle righe senza IVA'
                    "
                  >
                    {{ salvandoNatura ? "Salvataggio…" : "Salva la Natura" }}
                  </button>
                  <!-- Aggiornare qui è anche esportare il PDF: serve X. -->
                  <button
                    v-if="can(areaDocumento, 'X')"
                    type="button"
                    id="save-quote"
                    class="primary-btn"
                    @click="onUpdateQuoteAndGeneratePdf"
                    :disabled="!canSave || savingQuote"
                    :aria-disabled="!canSave || savingQuote ? 'true' : 'false'"
                    :title="
                      savingQuote
                        ? 'Salvataggio ed esportazione in corso'
                        : modificaChiusa
                        ? 'Ordine pagato e in consegna: non modificabile'
                        : righeSenzaNatura
                        ? 'Scegli la Natura delle righe senza IVA'
                        : !canSave
                        ? 'Modifica almeno uno dei campi per aggiornare il preventivo'
                        : 'Aggiorna preventivo ed esporta PDF'
                    "
                  >
                    {{
                      savingQuote ? "Salvataggio…" : "Aggiorna ed esporta PDF"
                    }}
                  </button>

                  <!-- [sc-48351 / task 7] Finché è una bozza si salva com'è,
                       senza PDF e senza che articoli e modalità siano a posto:
                       è il lavoro a metà che si mette da parte. Il pulsante
                       sparisce appena la bozza esce dalle bozze. -->
                  <button
                    v-if="features.quoteDrafts && eBozza"
                    type="button"
                    id="save-quote-draft"
                    class="btn-secondary"
                    @click="onSalvaComeBozza"
                    :disabled="!canSaveDraft || savingQuote"
                    :title="
                      modificaChiusa
                        ? 'Ordine pagato e in consegna: non modificabile'
                        : !isDirty
                        ? 'Non c\'è niente di nuovo da salvare'
                        : 'Salva la bozza così com\'è, senza generare il PDF'
                    "
                  >
                    {{ savingQuote ? "Salvataggio…" : "Salva come bozza" }}
                  </button>

                  <button
                    v-if="can(areaDocumento, 'X')"
                    type="button"
                    id="send-email-quote"
                    @click="onSendDocumentEmail"
                    :disabled="!pdfUploaded || sendingEmail"
                    :title="
                      !pdfUploaded
                        ? 'Genera prima il PDF per poter inviare l\'email'
                        : 'Invia documento via email/PEC al cliente'
                    "
                  >
                    <i class="fa-solid fa-envelope"></i>
                    {{
                      sendingEmail
                        ? "Invio in corso..."
                        : "Invia email al cliente"
                    }}
                  </button>

                  <router-link
                    :to="
                      isOrderRoute
                        ? `/home/ordini/dettaglio/${quoteId}`
                        : `/home/preventivi/dettaglio/${quoteId}`
                    "
                    class="secondary-btn"
                    :title="
                      isOrderRoute
                        ? 'Torna alla visualizzazione ordine'
                        : 'Torna alla visualizzazione preventivo'
                    "
                  >
                    <i class="fa-solid fa-eye"></i>
                    Visualizza
                  </router-link>
                </template>
              </div>
            </div>
            <!-- /.quote-work-col -->
          </div>
          <!-- /.quote-work-row -->
        </form>
      </div>
      <!-- /.create-quote-main-col -->
    </div>
    <!-- /.create-quote-wrapper -->
    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">
          <template v-if="features.quoteDrafts && eBozza">
            Puoi salvare quello che hai scritto nella bozza, e finirla dopo.
          </template>
          <template v-else>Le modifiche non salvate andranno perse.</template>
        </p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button
            v-if="features.quoteDrafts && eBozza && !modificaChiusa"
            class="secondary-btn"
            :disabled="savingQuote"
            @click="salvaBozzaEdEsci"
          >
            {{ savingQuote ? "Salvataggio…" : "Salva nella bozza ed esci" }}
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            {{
              features.quoteDrafts && eBozza ? "Esci senza salvare" : "Conferma"
            }}
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modale "Crea articolo": crea l'articolo e lo auto-seleziona nel preventivo -->
    <CreateItemModal
      v-model="showCreateItemModal"
      :initial-name="searchQueryItem"
      @created="onItemCreated"
    />
  </main>
</template>

<script>
import { linkMaps } from "@/utilities/mapsLink";
import {
  NATURE,
  riferimentoPredefinito,
  etichettaIva,
} from "@/utilities/nature";
import logoPath from "@/assets/logo-liberotratto.png";
// import crypto from "crypto";
import axiosInstance from "@/axiosInstance";
import { formatDocName as docNameLabel } from "@/utilities/documentName";
import { ordineNonModificabile } from "@/utilities/pagamenti";
import { generateQuotePdf } from "@/utilities/generateQuotePdf";
import {
  isSoldByMeasure,
  measureGroupOf,
  requestedQuantityNote,
} from "@/utilities/quoteItemUnits";
import {
  missingStockFields,
  hasIncompleteStockData,
  itemPerBox,
} from "@/utilities/itemStockData";
import debounce from "lodash/debounce";
// import { nextTick } from "process";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import {
  formatToLocalDate,
  toDateLocal,
  formatDecimal,
} from "../utilities/formatting";
import SearchableCombo from "./SearchableCombo.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import CreateItemModal from "@/components/CreateItemModal.vue";
import FlatPickr from "vue-flatpickr-component";
import { Italian } from "flatpickr/dist/l10n/it.js";

// Chiavi cache namespaced
const asKey = (id) => String(id ?? ""); // normalizza sempre a stringa
const KEY = {
  STOCK: (id) => `STOCK:${asKey(id)}`,
};

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

/* Gruppo di misura dell'articolo: «area» (mq), «linear» (ml), «piece».
   Comanda il dato dell'articolo — `salesUnit`, o i mq/ml per scatola per le
   schede caricate prima che esistesse. La categoria merceologica resta
   l'ultima risorsa, per gli articoli che non dichiarano nulla: se decidesse
   lei, un articolo venduto a mq in una categoria fuori elenco chiederebbe i
   pezzi mentre l'API si aspetta le scatole. */
function groupOf(item) {
  const fromData = measureGroupOf(item);
  if (fromData) return fromData;
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(item?.category)) return group;
  }
  return "generic";
}

/* Quantità in mq/ml corrispondente a N scatole, come multiplo esatto di
   perBox (evita 3 x 1,44 = 4,319999...). Stringa pronta per il campo input:
   punto decimale, zeri finali rimossi. */
function quantityFromBoxes(boxes, perBox) {
  const n = Number(boxes) || 0;
  const scaled = Math.round((Number(perBox) || 0) * 10000);
  if (!n || !scaled) return "";
  return ((n * scaled) / 10000)
    .toFixed(4)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
}

/* Scatole intere necessarie per una quantità in mq/ml.
   Arrotonda per eccesso (si comprano scatole intere) ma con tolleranza: senza,
   l'errore float o l'arrotondamento del campo fa scattare una scatola in più
   (es. 4,32 / 1,44 = 3.0000000000000004 -> ceil = 4 scatole invece di 3). */
function boxesFromQuantity(quantity, perBox) {
  const qty = Number(quantity) || 0;
  const step = Number(perBox) || 0;
  if (!step || qty <= 0) return 0;
  const ratio = qty / step;
  const nearest = Math.round(ratio);
  const tolerance = Math.min(0.005 / step, 0.01);
  return Math.abs(ratio - nearest) <= tolerance ? nearest : Math.ceil(ratio);
}

// mq/ml persistiti dall'API: null sulle righe storiche e sugli articoli a pezzo
function mqFromApi(value) {
  const n = Number(value);
  return n > 0 ? n : null;
}

// mq/ml per scatola con fallback: se l'articolo non ha il dato, si assume
// 1 unità per scatola — stessa assunzione dell'API nel calcolo del totale
// (COALESCE(sqmPerBox, 1)) — così l'articolo resta comunque preventivabile.
function perBoxOrFallback(perBox) {
  const n = Number(perBox) || 0;
  return n > 0 ? n : 1;
}

export default {
  name: "EditQuotePage",
  components: { SearchableCombo, StatusSelect, CreateItemModal, FlatPickr },
  beforeRouteLeave(to) {
    if (
      this.readOnly ||
      this.navigationConfirmed ||
      to.name === "login" ||
      !this.isDirty
    ) {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  props: {
    quoteId: [String, Number],
    readOnly: { type: Boolean, default: false },
  },
  created() {
    // [sc-48351 / task 48786] Clienti e articoli servono a scegliere, e si
    // chiedono solo a chi il ruolo li fa vedere: il magazziniere apre il
    // dettaglio di un ordine senza l'area Clienti, e la richiesta finiva in
    // «Il tuo ruolo non permette questa operazione». Il nome del cliente
    // arriva comunque col preventivo (`clientName`).
    if (this.can("clienti", "V")) {
      this.$store
        .dispatch("fetchClients", {
          limit: 10000,
        })
        .catch((err) => {
          // evita Uncaught (in promise)
          console.error("Impossibile caricare i clienti:", err.message);
          // puoi mostrare un toast o impostare un flag di errore
        });
    }
    // this.$store.dispatch("fetchOperators");
    if (this.can("listini", "V")) {
      this.$store.dispatch("fetchItems", { limit: 10000 }).catch((err) => {
        // evita Uncaught (in promise)
        console.error("Impossibile caricare gli articoli:", err.message);
        // puoi mostrare un toast o impostare un flag di errore
      });
    }
    this.debouncedFetchItems = debounce(this.fetchItems, 200);
  },
  computed: {
    // Indirizzo mostrato nel campo: "Ritiro in sede" quando non è consegna
    // (solo visuale; il valore reale formData.location.address resta invariato)
    addressModel: {
      get() {
        return this.formData.deliveryType === "ritiro"
          ? "Ritiro in sede"
          : this.formData.location.address;
      },
      set(v) {
        if (this.formData.deliveryType === "consegna") {
          this.formData.location.address = v;
        }
      },
    },
    // Ponte UI per la select "Modalità di consegna": il payload resta
    // "consegna" | "ritiro" | "". Stessa logica di azzeramento indirizzo.
    deliveryTypeModel: {
      get() {
        return this.formData.deliveryType;
      },
      set(v) {
        this.formData.deliveryType = v || "";
        if (this.formData.deliveryType !== "consegna") {
          this.formData.location.address = "";
        }
      },
    },
    /** Funzioni accese per l'azienda collegata, dal piano via store. */
    features() {
      return this.$store.getters["company/features"];
    },

    /** [sc-48351 / task 7] Questo preventivo è ancora una bozza. */
    eBozza() {
      return this.offerState === "Bozza";
    },

    /**
     * Per risalvare una bozza basta che sia cambiato qualcosa: i campi che
     * mancano glieli richiede il passaggio di stato, non il salvataggio.
     */
    canSaveDraft() {
      return (
        this.eBozza &&
        this.isDirty &&
        !this.modificaChiusa &&
        !this.righeSenzaNatura
      );
    },

    canSave() {
      // articoli obbligatori + modalità (consegna/ritiro) obbligatoria
      const hasItems = (this.formData.items || []).length > 0;
      return (
        hasItems &&
        !!this.formData.deliveryType &&
        this.isDirty &&
        !this.dupLoading &&
        !this.modificaChiusa &&
        !this.righeSenzaNatura
      );
    },
    // true solo se lo stato attuale differisce dallo snapshot iniziale.
    // Finché lo snapshot non è acquisito (originalSig vuoto) → non "sporco".
    isDirty() {
      if (!this.originalSig) return false;
      return this.computeSignature() !== this.originalSig;
    },
    isAdmin() {
      return this.$store.state.currentUser?.isAdmin ?? false;
    },
    isOrderRoute() {
      return ["edit-order", "view-order"].includes(this.$route.name);
    },
    can() {
      return this.$store.getters["auth/can"];
    },
    /** [sc-48351 / task 48786] Un ordine è un preventivo confermato. */
    areaDocumento() {
      return this.isOrderRoute ? "ordini" : "preventivi";
    },
    isAddressValid() {
      return !!(this.formData.location?.address || "").trim();
    },

    // selectedItem() {
    //   /* Se è stato scelto un articolo di magazzino */
    //   if (this.newItem.itemId) {
    //     return this.getItemFromStore(this.newItem.itemId); // ← già fa cache
    //   }
    //   /* Se è stato scelto un articolo FUORI magazzino */
    //   if (this.newItem.outstockId) {
    //     return (
    //       this.outstocks.find((o) => o.id === this.newItem.outstockId) || {}
    //     );
    //   }
    //   /* Nessuna selezione */
    //   return {};
    // },

    selectedItem() {
      if (this.newItem.itemId) {
        return this.getItemFromStore(this.newItem.itemId, "STOCK");
      }
      return {};
    },

    selectedItemGroup() {
      // Da qui dipendono l'etichetta del campo quantità e il campo in cui
      // finisce ciò che l'utente digita: se lo decidesse la categoria, un
      // articolo venduto a mq fuori dalle categorie merceologiche note
      // chiederebbe i pezzi mentre l'API si aspetta le scatole, e la quantità
      // andrebbe persa. La categoria resta solo per gli articoli che non
      // dichiarano nulla.
      return groupOf(this.selectedItem);
    },
    mqFieldPlaceholder() {
      return this.selectedItemGroup === "linear"
        ? "Inserisci i ml"
        : "Inserisci i mq";
    },
    mqFieldLabel() {
      if (this.selectedItemGroup === "linear") return "Quantità ML";
      return "Quantità Mq";
    },
    quantityLabel() {
      if (this.selectedItemGroup === "area") return "Quantità scatole";
      if (this.selectedItemGroup === "linear") return "Quantità confezioni";
      if (
        this.selectedItem.category === "Fuori Magazzino" &&
        this.selectedItem.subCategory === "piastrelle"
      )
        return "Quantità (mq)";
      if (this.selectedItem.category === "Fuori Magazzino") return "Quantità";
      return "Quantità pezzi";
    },

    // quantityInput: {
    //   get() {
    //     return this.selectedItem.category === "piastrelle"
    //       ? this.newItem.boxQuantity // lavoro in scatole
    //       : this.newItem.pieces; // lavoro in pezzi
    //   },
    //   set(v) {
    //     const n = +v || 0;
    //     if (this.selectedItem.category === "piastrelle") {
    //       // console.log("selectedItem in quantityinput", this.selectedItem);
    //       this.newItem.boxQuantity = n; // scatole
    //       const ppb = this.selectedItem.piecesPerBox || 1;
    //       this.newItem.pieces = n * ppb; // pezzi calcolati
    //       // this.newItem.unitPrice;
    //     } else {
    //       this.newItem.pieces = n; // sanitari
    //     }

    //     // ricalcolo per FUORI MAGAZZINO: totale = unit × qty
    //     if (this.selectedItem.category === "Fuori Magazzino") {
    //       const isTiles = this.selectedItem.subCategory === "piastrelle";
    //       const base = isTiles
    //         ? Number(this.selectedItem.sqmPrice) || 0
    //         : Number(this.selectedItem.price) || 0;

    //       const unit = Number(this.adjustedPrice(base, this.newItem.iva));
    //       this.newItem.unitPrice = unit;

    //       const qty = this.newItem.pieces || 0; // anche per mq
    //       this.newItem.price = (unit * qty).toFixed(2); // mostra il totale nel campo
    //     }
    //   },
    // },

    quantityInput: {
      get() {
        const g = groupOf(this.selectedItem);
        if (g === "area" || g === "linear") return this.newItem.boxQuantity;
        return this.newItem.pieces;
      },
      set(v) {
        // scatole/confezioni/pezzi sono sempre interi: niente 2,5 scatole
        const n = Math.max(0, Math.round(+v || 0));
        const g = groupOf(this.selectedItem);

        if (g === "area") {
          this.newItem.boxQuantity = n;
          const ppb = this.selectedItem.piecesPerBox || 1;
          this.newItem.pieces = n * ppb;
        } else if (g === "linear") {
          this.newItem.boxQuantity = n;
          this.newItem.pieces = 0;
        } else {
          this.newItem.pieces = n;
        }

        if (this.selectedItem.category === "Fuori Magazzino") {
          const isTiles = this.selectedItem.subCategory === "piastrelle";
          const base = isTiles
            ? Number(this.selectedItem.sqmPrice) || 0
            : Number(this.selectedItem.price ?? this.newItem.unitPrice) || 0;

          const unit = Number(base) || 0; // prezzo unitario NETTO (IVA esclusa)
          this.newItem.unitPrice = unit;
          // this.newItem.pieces = n;

          const qty = this.newItem.pieces || 0;
          this.newItem.price = (unit * qty).toFixed(2);
        }
      },
    },

    /* Abilita "aggiungi articolo" solo con quantità numerica > 0 */
    /* [sc-48351] La riga si compila in ordine: prima l'articolo, poi la
       quantità, poi l'eventuale sconto. Invece di dirlo con delle frecce, i
       campi che verrebbero prima del loro turno restano spenti. */
    articoloScelto() {
      return Boolean(this.newItem.itemId);
    },

    quantitaInserita() {
      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";
      const qty = isAreaOrLinear
        ? Number(this.newItem.boxQuantity)
        : Number(this.newItem.pieces);
      return Number.isFinite(qty) && qty >= 1;
    },

    canAddItem() {
      return this.articoloScelto && this.quantitaInserita && this.naturaPronta;
    },

    // [sc-49176] Senza IVA la riga vuole la Natura dell'operazione.
    naturaPronta() {
      return Number(this.newItem.iva) !== 0 || !!this.newItem.natura;
    },

    // [sc-49176 / task 11] Quante righe hanno una Natura scelta e non salvata.
    naturaDaSalvare() {
      return Object.keys(this.natureDaSalvare).length;
    },

    // [sc-49176] Righe a 0% senza Natura (storiche, salvate prima che la
    // Natura esistesse): finché ce n'è una non si salva, l'API le rifiuta.
    righeSenzaNatura() {
      return (this.formData.items || []).filter((r) => this.rigaSenzaNatura(r))
        .length;
    },

    /* Articolo a mq/ml con la scheda incompleta: si può comunque preventivare,
       ma senza mq/scatola e pz/scatola il preventivo non è confermabile.
       Restituisce l'avviso e la rotta per completare la scheda. */
    missingBoxDataInfo() {
      const item = this.selectedItem;
      if (!this.showMqField || !item?.id) return null;
      const missing = missingStockFields(item);
      if (!missing.length) return null;
      return {
        text: `Scheda articolo incompleta (manca ${missing.join(
          ", "
        )}): puoi aggiungere l'articolo al preventivo, ma completa la scheda prima di confermarlo al cliente.`,
        route: {
          name: "listini-edit-item",
          params: { category: item.category, itemId: item.id },
        },
      };
    },

    /* mq/ml per scatola dell'articolo selezionato: passo di incremento del
       campo mq (una freccia = una scatola) */
    mqPerBox() {
      const s = this.selectedItem || {};
      const g = groupOf(s);
      const perBox =
        g === "linear" ? Number(s.mlPerBox) || 0 : Number(s.sqmPerBox) || 0;
      return perBox > 0 ? perBox : 0;
    },

    /* Riepilogo sotto il campo mq: l'operatore scrive i mq, ma il preventivo va
       a scatole intere. L'hint rende esplicito cosa viene effettivamente venduto. */
    mqSummary() {
      if (!this.showMqField) return "";
      const perBox = this.mqPerBox;
      // dato mancante: al posto del riepilogo si mostra l'avviso con il link
      if (!perBox) return "";
      const boxes = Number(this.newItem.boxQuantity) || 0;
      const isLinear = groupOf(this.selectedItem) === "linear";
      const unit = isLinear ? "ml" : "mq";
      if (!boxes) return `${formatDecimal(perBox)} ${unit}/scatola`;
      const label = isLinear ? "confezioni" : "scatole";
      const total = formatDecimal(quantityFromBoxes(boxes, perBox));
      const ppb = Number(this.selectedItem?.piecesPerBox) || 0;
      const pieces = !isLinear && ppb ? ` · ${boxes * ppb} pz totali` : "";
      return `${boxes} ${label} = ${total} ${unit}${pieces}`;
    },

    /* Anteprima riga (nuova semantica IVA esclusa): netto applicato, quantità
       effettiva, e importi imponibile/imposta/totale dell'intera riga. Valori 0
       se la quantità non è ancora valida (nessun NaN). */
    previewAppliedNet() {
      const personalized = this.optionalNonNegativeNumber(
        this.newItem.discountedPrice
      );
      return personalized !== null
        ? personalized
        : Number(this.newItem.unitPrice ?? this.newItem.price) || 0;
    },
    previewEffectiveQty() {
      const group = this.selectedItemGroup;
      if (group === "area" || group === "linear") {
        // Si fattura la scatola intera: l'anteprima mostra l'importo che il
        // cliente pagherà davvero, non quello dei mq che ha chiesto. I mq
        // digitati valgono solo se le scatole non sono calcolabili.
        const perBox = Number(this.mqPerBox) || 0;
        const boxes = Number(this.newItem.boxQuantity) || 0;
        if (perBox > 0) return quantityFromBoxes(boxes, perBox);
        const typed = this.typedMqQuantity();
        if (typed !== null) return Number(typed) || 0;
        return quantityFromBoxes(boxes, perBoxOrFallback(perBox));
      }
      return Number(this.newItem.pieces) || 0;
    },
    linePreview() {
      const net = Number(this.previewAppliedNet) || 0;
      const qty = Number(this.previewEffectiveQty) || 0;
      const iva = Number(this.newItem.iva) || 0;
      const taxable = +(net * qty).toFixed(2);
      const tax = +((taxable * iva) / 100).toFixed(2);
      const gross = +(taxable + tax).toFixed(2);
      return { taxable, tax, gross };
    },
    previewFormatted() {
      const p = this.linePreview;
      return {
        taxable: formatDecimal(p.taxable),
        tax: formatDecimal(p.tax),
        gross: formatDecimal(p.gross),
      };
    },

    showMqField() {
      const s = this.selectedItem || {};
      if (!s || !s.category) return false;
      const g = groupOf(s);
      return g === "area" || g === "linear";
    },

    clients() {
      return this.$store.getters["clients/list"] ?? [];
    },
    items() {
      return this.$store.getters["items/list"] ?? [];
    },

    filteredClients() {
      const q = (this.searchQueryClient || "").toLowerCase().trim();
      const list = this.clients || [];
      let res = q
        ? list
            .filter((c) => (c.clientName || "").toLowerCase().includes(q))
            .slice(0, 8)
        : list.slice(0, 8);

      const selId = this.formData.clientId;
      const selected = list.find((c) => c.id === selId);
      if (selected && !res.some((c) => c.id === selId)) {
        res = [selected, ...res].slice(0, 8);
      }
      return res;
    },

    // Tutti i nomi da mostrare nella colonna "Articolo"
    itemNames() {
      return (this.formData.items || []).map(
        (row) => this.getItemName(row.itemId, row.category) || ""
      );
    },

    // Lunghezza massima in caratteri tra i nomi
    maxItemLen() {
      return this.itemNames.reduce((m, s) => Math.max(m, s.length), 0);
    },

    // Larghezza dinamica della colonna "Articolo" in ch, con limiti per non rompere il layout
    // - min 180px (leggibile anche con nomi brevi)
    // - valore centrale in ch (caratteri) = maxArtLen + 6 (un po' di respiro)
    // - max 52vw (mai oltre metà viewport nel modale)
    itemColWidth() {
      const ch = Math.max(10, Math.min(this.maxItemLen + 6, 72)); // clamp "logico"
      return `clamp(180px, ${ch}ch, 52vw)`;
    },
    showSelectedItemOrderAlert() {
      return this.isOrderableOutOfStock(this.selectedItem);
    },
    selectedItemOrderLabel() {
      return this.itemSearchLabel(this.selectedItem);
    },

    isStockLocked() {
      return !!this.newItem.itemId;
    },
    clientIdModel: {
      get() {
        const v = this.formData.clientId;
        return v === null || v === undefined ? "" : String(v);
      },
      set(v) {
        // se preferisci tenerlo number nello stato:
        this.formData.clientId = v ? Number(v) : "";
      },
    },
  },

  data() {
    return {
      dateConfig: {
        locale: Italian,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: true,
        monthSelectorType: "static",
      },
      showBackModal: false,
      /** [sc-48351 / task 7] Lo stato d'offerta del preventivo aperto. */
      offerState: null,
      pendingNavigation: null,
      navigationConfirmed: false,

      originalSig: "", // firma dello stato iniziale del preventivo, per rilevare modifiche

      showDuplicateModal: false,
      lastDuplicateQuoteInfo: { quoteId: null, address: null },
      showDupDocs: false,
      dupDocuments: [],
      // Modale "Crea articolo" (richiamo da campo ricerca articolo)
      showCreateItemModal: false,
      dupLoading: false,
      dupError: "",
      dupQuote: null,

      showDropdown: false,
      searchQueryItem: "",
      searchQueryClient: "",
      // mq/ml digitati dall'operatore: valore libero, non riscritto dalla UI.
      // Le scatole del preventivo sono derivate da qui (vedi watcher mqInput).
      mqInput: "",
      filteredItems: [],
      // Ricerca articolo: loading + paginazione risultati (fix #2)
      itemsLoading: false,
      itemsPage: 1, // ultima pagina caricata con successo
      itemsRequestedPage: null, // pagina della richiesta in volo (pending)
      itemsTotalPages: 1,
      itemsTotalItems: null,
      itemsSearchQuery: "",
      itemsRequestId: 0,
      pendingOrderItems: {},
      orderNote: "",
      // selectedItemById: {},
      selectedByKey: {},
      // selectedOutstockById: {},
      panelVisible: true,
      activeSearch: null, // 'client' o 'item' o null
      selectedIndex: -1, // Indice dell'opzione attualmente evidenziata
      response: "",
      image: logoPath,

      formData: {
        location: {
          name: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
        },
        clientId: "",
        items: [],
        date: "",
        payment_method: "",
        technical_queries: "",
        deliveryType: "",
        hasPackagingFee: false,
      },
      newItem: {
        itemId: null,
        boxQuantity: 0, // ← scatole, solo per piastrelle
        pieces: 0, // ← pezzi fisici (sanitari) o calcolati
        price: "",
        discountedPrice: "",
        iva: 22,
        natura: null,
        category: "",
        // sqmPerBox: "", // solo per piastrelle
      },
      // [sc-48692] Salvataggio in corso: il pulsante non si preme due volte.
      savingQuote: false,
      // [sc-48692] Ordine pagato con la consegna iniziata: sola lettura.
      modificaChiusa: false,
      // [sc-49176 / task 11] Su un ordine chiuso: id della riga → Natura
      // scelta e non ancora salvata.
      natureDaSalvare: {},
      salvandoNatura: false,
      errorMessage: "",
      successMessage: "",
      selectedPlaceId: "",
      paymentOptions: [
        { value: "Contanti", label: "Contanti" },
        { value: "Carta di credito", label: "Carta di credito" },
        { value: "Bonifico", label: "Bonifico" },
        { value: "Scegli successivamente", label: "Scegli successivamente" },
      ],
      deliveryOptions: [
        { value: "consegna", label: "Presso cliente", icon: "fa-truck" },
        { value: "ritiro", label: "Ritiro", icon: "fa-store" },
      ],
      // [sc-49176] Lo zero è «Senza IVA»: vuole la Natura dell'operazione.
      ivaOptions: [
        { value: 22, label: "22%" },
        { value: 10, label: "10%" },
        { value: 5, label: "5%" },
        { value: 4, label: "4%" },
        { value: 0, label: "Senza IVA" },
      ],
      naturaOptions: NATURE.map((n) => ({
        value: n.codice,
        label: `${n.codice} – ${n.descrizione}`,
      })),
      pdfUploaded: false,
      lastUploadedDocName: "",
      sendingEmail: false,
    };
  },
  methods: {
    linkMaps,
    etichettaIva,
    riferimentoPredefinito,
    // [sc-49176] Una riga a 0% senza Natura: la tabella la fa scegliere.
    rigaSenzaNatura(riga) {
      return Number(riga.iva) === 0 && !riga.natura;
    },
    // [sc-49176] Natura scelta su una riga storica: il riferimento normativo
    // è quello del codice.
    scegliNatura(riga, codice) {
      riga.natura = codice;
      riga.legalReference = riferimentoPredefinito(codice) ?? "";
      // [sc-49176 / task 11] Su un ordine chiuso la scelta si salva da sola,
      // con «Salva la Natura»: si ricorda quale riga è stata toccata.
      if (this.modificaChiusa && riga.quoteItemId != null) {
        const scelte = { ...this.natureDaSalvare };
        if (codice) scelte[riga.quoteItemId] = codice;
        else delete scelte[riga.quoteItemId];
        this.natureDaSalvare = scelte;
      }
    },
    // [sc-49176 / task 11] Riga di un ordine chiuso con la Natura scelta e non
    // ancora salvata: la tendina resta, così la scelta si può correggere.
    naturaInSospeso(riga) {
      return (
        this.modificaChiusa &&
        riga.quoteItemId != null &&
        riga.quoteItemId in this.natureDaSalvare
      );
    },
    // [sc-49176 / task 11] Salva la sola Natura delle righe toccate, anche su
    // un ordine pagato e in consegna: il resto dell'ordine non cambia.
    async salvaNatura() {
      const righe = Object.entries(this.natureDaSalvare).map(
        ([id, natura]) => ({ id: Number(id), natura })
      );
      if (!righe.length || this.salvandoNatura) return;
      this.salvandoNatura = true;
      try {
        await axiosInstance.patch(`/quote/${this.quoteId}/natura-righe`, {
          righe,
        });
        this.$toast.success("Natura salvata sulle righe senza IVA.");
        this.natureDaSalvare = {};
        await this.fetchQuoteData();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message || "Impossibile salvare la Natura."
        );
      } finally {
        this.salvandoNatura = false;
      }
    },
    goBack() {
      this.pendingNavigation = null;
      // Nessuna modifica (o sola lettura): torna indietro senza conferma.
      if (this.readOnly || !this.isDirty) {
        this.navigationConfirmed = true;
        this.$router.back();
        return;
      }
      this.showBackModal = true;
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        this.$router.back();
      }
    },
    // Guardia reload/chiusura tab nativa del browser: mostra il dialog
    // nativo di conferma quando si sta modificando (salta in sola lettura).
    handleBeforeUnload(e) {
      if (this.readOnly || !this.isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    },
    // sanitize stringhe nel payload prima dell'update
    sanitizeStr(s, { maxLen = 5000 } = {}) {
      if (s == null) return "";
      let out = String(s).trim();

      // rimuovi caratteri di controllo (tranne tab/newline)
      // out = out.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, " ");

      // strip tag HTML basilare
      out = out.replace(/<[^>]*>/g, " ");

      // normalizza spazi multipli
      out = out.replace(/\s{2,}/g, " ").replace(/\n{3,}/g, "\n\n");

      // limita lunghezza
      if (out.length > maxLen) out = out.slice(0, maxLen);

      return out;
    },
    cleanNumber(n, { min = null } = {}) {
      const v = Number(n);
      if (!Number.isFinite(v)) return 0;
      if (min != null && v < min) return min;
      return v;
    },

    // Riconosce se è piastrella (anche per Fuori Magazzino)
    isTilesObj(src) {
      return (
        src?.category === "piastrelle" ||
        (src?.category === "Fuori Magazzino" &&
          src?.subCategory === "piastrelle")
      );
    },

    // Nome da mostrare ovunque (tabella modale + PDF)
    nameForDisplay(src) {
      return this.itemDisplayFromObj(src || {}).title || "Sconosciuto";
    },

    // Capitallizza la prima lettera
    Cap(s) {
      return (s || "")
        .toString()
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    /**
     * Ritorna { title, meta } a partire dall'oggetto articolo già risolto
     * - Piastrelle (magazzino): titolo = itemName, meta = measureDesc (oppure WxH cm)
     * - Fuori Magazzino/Piastrelle: meta fisso "Piastrelle"
     * - Sanitari/Altro: meta = tipo/posizione (fallback subCategory)
     */
    itemDisplayFromObj(it = {}) {
      // --- GRUPPO AREA (piastrelle, rivestimenti, parquet, laminati, decking) ---
      if (groupOf(it) === "area") {
        const title = it.itemName || "Articolo";
        const dim =
          it.measureDesc ||
          it.formatLabel ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null);
        const thickStr = it.thicknessMm
          ? `${parseFloat(it.thicknessMm)}mm`
          : null;
        const meta =
          [
            dim && !title.includes(dim) ? dim : null,
            it.surface,
            thickStr && !title.includes(thickStr) ? thickStr : null,
            it.color,
          ]
            .filter(Boolean)
            .join(" · ") || it.category;
        return { title, meta };
      }

      // --- FUORI MAGAZZINO: PIASTRELLE ---
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const dim =
          it.measureDesc ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null);
        const title =
          [it.itemName, dim].filter(Boolean).join(" \u2192 ").trim() ||
          "Articolo fuori magazzino";
        return {
          title,
          meta: "Piastrelle",
        };
      }

      // --- SANITARI / ALTRO (STOCK) ---
      const title =
        it.itemName ||
        [it.brand, it.subBrand].filter(Boolean).join(" \u2192 ").trim() ||
        "Articolo fuori magazzino";

      // accetta anche eventuali chiavi legacy/type/position e usa category come fallback
      const T = this.Cap(it.fixtureType || it.type || it.category || "");
      const P =
        (it.fixturePosition || it.position) === "terra"
          ? "a terra"
          : this.Cap(it.fixturePosition || it.position || "");

      const metaParts = [T, P].filter(Boolean);
      const meta =
        (metaParts.length ? metaParts.join(" ") : null) ||
        this.Cap(it.subCategory || it.category || ""); // <- altro fallback

      return { title, meta };
    },
    /**
     * Ritorna { title, meta } cercando nel negozio partendo da id + hint categoria
     */
    displayForId(id, categoryHint) {
      if (!id) return { title: "Sconosciuto", meta: "" };
      const src = this.getItemFromStore(id, categoryHint) || {};
      if (src && src.id != null) return this.itemDisplayFromObj(src);

      // fallback: prova a leggere la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        return this.itemDisplayFromObj({
          id,
          category: row.category,
          itemName: row.itemName,
          brand: row.brand,
          subBrand: row.subBrand,
          subCategory: row.subCategory,
        });
      }
      return { title: "Sconosciuto", meta: "" };
    },

    // normalizza solo i campi "di valore" (UI effimera esclusa)
    normalizeForCompare() {
      const f = this.formData || {};
      const L = f.location || {};
      const locValid = (L.placeId || L.place_id) && L.latitude && L.longitude;
      const loc = locValid
        ? {
            placeId: L.placeId || L.place_id,
            lat: Number(L.latitude),
            lng: Number(L.longitude),
          }
        : null;

      const items = (f.items || [])
        .map((r) => {
          const id = r.itemId;
          const qty = Number(r.boxQuantity || r.pieces || 0);
          const price = Number(r.unitPrice ?? r.price ?? 0);
          const iva = Number(r.iva ?? 22);
          // [sc-49176] Anche la Natura scelta su una riga storica è una modifica.
          const natura = r.natura || null;
          const rif = r.legalReference || null;
          return { cat: r.category || "", id, qty, price, iva, natura, rif };
        })
        .sort(
          (a, b) =>
            a.cat.localeCompare(b.cat) ||
            String(a.id || "").localeCompare(String(b.id || ""))
        );

      return {
        clientId:
          f.clientId === "" || f.clientId === null || f.clientId === undefined
            ? null
            : Number(f.clientId),
        date: f.date || "",
        payment_method: f.payment_method || "",
        technical_queries: f.technical_queries || "",
        hasPackagingFee: !!f.hasPackagingFee,
        deliveryType: f.deliveryType || "",
        location: loc,
        items,
      };
    },

    // normalizza la location arrivata dall’API a camelCase + latitude/longitude
    normalizeLocFromApi(raw) {
      if (!raw) {
        return {
          name: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
          link: "",
        };
      }
      const obj =
        typeof raw === "string"
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return {};
              }
            })()
          : raw;

      const placeId = obj.placeId || obj.place_id || "";
      // supporta lat/lng legacy o geometry di Google
      const latRaw =
        obj.latitude ?? obj.lat ?? obj?.geometry?.location?.lat ?? "";
      const lngRaw =
        obj.longitude ?? obj.lng ?? obj?.geometry?.location?.lng ?? "";
      let latitude =
        typeof latRaw === "function" ? Number(latRaw()) : Number(latRaw);
      let longitude =
        typeof lngRaw === "function" ? Number(lngRaw()) : Number(lngRaw);

      // fallback: estrae lat/lng dall'URL del link (formato query=lat%2Clng)
      if (
        (!latRaw ||
          !lngRaw ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)) &&
        obj.link
      ) {
        const m = obj.link.match(/[?&]query=([-\d.]+)(?:%2C|,)([-\d.]+)/i);
        if (m) {
          latitude = Number(m[1]);
          longitude = Number(m[2]);
        }
      }

      return {
        name: obj.name || "",
        address: obj.address || obj.formatted_address || "",
        placeId,
        latitude: Number.isFinite(latitude) ? latitude : "",
        longitude: Number.isFinite(longitude) ? longitude : "",
        link: obj.link || "",
      };
    },

    computeSignature() {
      // stringify stabile: gli oggetti sono già normalizzati e ordinati
      return JSON.stringify(this.normalizeForCompare());
    },
    captureSnapshot() {
      this.originalSig = this.computeSignature();
    },

    async loadDuplicateQuote() {
      const id = this.lastDuplicateQuoteInfo?.quoteId;
      if (!id) return;

      this.dupLoading = true;
      this.dupError = "";
      this.dupQuote = null;

      try {
        const { data } = await axiosInstance.get(`/quote/${id}`, {
          withCredentials: true,
        });

        // location normalizzata
        let parsedLocation = null;
        try {
          parsedLocation =
            typeof data?.quote?.location === "string"
              ? JSON.parse(data.quote.location)
              : data?.quote?.location || null;
        } catch {
          parsedLocation = null;
        }

        // items normalizzati per il modale
        const items = (data?.quote?.quote_items || [])
          .filter((qi) => !!qi.item)
          .map((qi) => {
            const cat = qi.item.category;
            const isBoxBased = isSoldByMeasure(qi.item);
            return {
              category: cat,
              itemName: qi.item.itemName || qi.item.subBrand,
              brand: qi.item.subBrand,
              boxQuantity: isBoxBased ? qi.quantity : 0,
              pieces: isBoxBased ? 0 : qi.quantity,
              mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
              iva: qi.iva ?? 22,
              natura: qi.natura ?? null,
              legalReference: qi.legalReference ?? null,
              price: Number(qi.price ?? 0),
              discountedPrice:
                qi.discountedPrice != null ? Number(qi.discountedPrice) : null,
            };
          });

        this.dupQuote = {
          ...data.quote,
          location: parsedLocation,
          totalPrice: data?.quote?.totalPrice ?? null,
          items,
        };
        this.dupDocuments = data?.quote?.documents || [];
      } catch (e) {
        this.dupError =
          e?.response?.data?.message ||
          "Impossibile caricare il preventivo esistente.";
      } finally {
        this.dupLoading = false;
      }
    },

    openDuplicateModal(quoteId, address) {
      this.lastDuplicateQuoteInfo = {
        quoteId: quoteId ?? null,
        address: address ?? null,
      };
      this.dupError = "";
      this.dupQuote = null;
      this.showDuplicateModal = true;
      if (this.lastDuplicateQuoteInfo.quoteId) this.loadDuplicateQuote();
    },
    closeDuplicateModal() {
      this.showDuplicateModal = false;
    },

    number(v) {
      return (typeof v === "number" ? v : Number(v || 0)).toFixed(2);
    },
    money(v) {
      return `€ ${this.number(v)}`;
    },
    formatDocName(name) {
      // [sc-48497 / task 48531] La pulizia sta in un posto solo: quattro
      // pagine la facevano ognuna per conto suo, e il prefisso dell'azienda
      // andava tolto in tutte e quattro.
      return docNameLabel(name);
    },
    formatToLocalDate,

    onChangeAddress() {
      this.showDuplicateModal = false;
      this.$nextTick(() => document.getElementById("autocomplete")?.focus());
    },

    // In Edit carico il preventivo in conflitto e NE COPIO i contenuti nella form attuale,
    // svuotando l'indirizzo: salverai poi con il tuo update
    async onDuplicateWithEdit() {
      const sourceId = this.lastDuplicateQuoteInfo?.quoteId;
      this.showDuplicateModal = false;
      if (!sourceId) return;

      try {
        if (!this.dupQuote || this.dupQuote.id !== sourceId) {
          this.dupLoading = true;
          const { data } = await axiosInstance.get(`/quote/${sourceId}`, {
            withCredentials: true,
          });
          let loc = data?.quote?.location;
          try {
            loc = typeof loc === "string" ? JSON.parse(loc) : loc;
          } catch (error) {
            this.$toast.error(
              "Impossibile caricare il dettaglio di questo preventivo."
            );
          }
          this.dupQuote = { ...data.quote, location: loc };
        }
        const q = this.dupQuote;

        // mappa quote_items -> formData.items (schema Edit)
        const mappedItems = (q.quote_items || [])
          .map((qi) => {
            if (qi.item) {
              const it = qi.item;
              const isBoxBased = isSoldByMeasure(it);
              return {
                itemId: it.id,
                itemName: it.itemName || it.subBrand,
                category: it.category,
                boxQuantity: isBoxBased ? qi.quantity : 0,
                pieces: isBoxBased ? 0 : qi.quantity,
                mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
                price: Number(qi.price),
                discountedPrice:
                  qi.discountedPrice != null
                    ? Number(qi.discountedPrice)
                    : null,
                iva: qi.iva ?? 22,
                natura: qi.natura ?? null,
                legalReference: qi.legalReference ?? null,
              };
            }
            return null;
          })
          .filter(Boolean);

        // cache per etichette
        (q.quote_items || []).forEach((qi) => {
          // if (qi.item) this.selectedItemById[qi.item.id] = qi.item;
          // if (qi.outstock) this.selectedItemById[qi.outstock.id] = qi.outstock;
          if (qi.item) this.selectedByKey[KEY.STOCK(qi.item.id)] = qi.item;
        });

        // compila la form con i dati del preventivo duplicato, ma svuota l'indirizzo
        this.formData = {
          ...this.formData,
          clientId: q.clientId,
          payment_method: q.payment_method || "",
          technical_queries: q.technical_queries || "",
          items: mappedItems,
          location: {
            name: "",
            address: "",
            placeId: "",
            latitude: "",
            longitude: "",
          },
        };
        this.newItem = this.resetNewItem();

        this.$nextTick(() => document.getElementById("autocomplete")?.focus());
        this.$toast.info(
          `Contenuto del preventivo #${q.id} copiato. Inserisci un indirizzo diverso e salva.`
        );
      } catch (e) {
        this.$toast.error("Impossibile duplicare questo preventivo ora.");
      } finally {
        this.dupLoading = false;
      }
    },

    measureDescFrom(w, h) {
      const W = Number(w),
        H = Number(h);
      if (Number.isFinite(W) && Number.isFinite(H) && W > 0 && H > 0) {
        return `${W}×${H} cm`;
      }
      return null;
    },

    rowCategoryGroup(row) {
      const it = this.getItemFromStore(row.itemId, row.category);
      return groupOf(it);
    },
    isStockTiles(row) {
      return this.rowCategoryGroup(row) === "area";
    },
    qtyCell(row) {
      const g = this.rowCategoryGroup(row);
      if (g === "area" || g === "linear") return Number(row.boxQuantity || 0);
      return Number(row.pieces || row.quantity || 0);
    },
    categoryHintForRow(row) {
      if (row?.category) return row.category;
      return "STOCK";
    },

    getItemFromStore(id) {
      if (!id) return {};

      const key = KEY.STOCK(id);

      // Store prima: inWarehouse sempre aggiornato rispetto alla cache del preventivo
      const stk =
        (this.items || []).find((i) => i.id === id) ||
        (this.filteredItems || []).find((i) => i.id === id);
      if (stk) {
        this.selectedByKey[key] = stk;
        return stk;
      }

      // Fallback alla cache per articoli non presenti nello store globale
      const cached = this.selectedByKey[key];
      if (cached && String(cached.id) === String(id)) {
        return cached;
      }

      return {};
    },

    // Mappa l'azione updateQuote dallo store
    ...mapActions([
      "fetchQuotes",
      "updateQuote",
      "createOrder",
      "uploadQuoteDocument",
      "sendQuoteDocumentEmail",
    ]),

    // formatToDateTimeLocal,

    itemSearchLabel(it) {
      if (!it) return "";
      return it?.itemName
        ? `${it.itemName} ${it.measureDesc || ""}`.trim()
        : `${it?.subBrand || ""} ${it?.fixtureType || ""} ${
            it?.fixturePosition || ""
          }`.trim();
    },

    // Riga secondaria del risultato ricerca: brand + formato + finiture.
    itemFormatFinish(it) {
      return [it?.brand, it?.formatLabel, it?.finish]
        .filter(Boolean)
        .join(" · ");
    },

    warehouseCount(item) {
      const stock = Number(item?.warehouse?.inWarehouse ?? item?.inWarehouse);
      return Number.isFinite(stock) ? stock : 0;
    },

    isOrderableOutOfStock(item) {
      return !!item?.id && this.warehouseCount(item) <= 0;
    },

    /* Giacenza mostrata nell'unità di vendita: mq/ml per gli articoli a
       scatola (le scatole restano nel tooltip), pezzi per gli altri. */
    /* Articolo venduto a scatola ma senza mq/ml per scatola: le scatole non
       sono calcolabili, quindi giacenza e quantità da ordinare non vengono
       mostrate (sarebbero numeri inventati). */
    hasBoxData(item) {
      const group = groupOf(item);
      if (group !== "area" && group !== "linear") return true;
      return itemPerBox(item) > 0;
    },

    rowHasBoxData(row) {
      return this.hasBoxData(this.rowStockItem(row));
    },

    stockLabel(item) {
      const count = this.warehouseCount(item);
      const group = groupOf(item);
      if (group !== "area" && group !== "linear") return `${count} sc.`;
      const perBox = itemPerBox(item);
      // senza mq/ml per scatola la giacenza in mq non è ricavabile
      if (!perBox) return "—";
      const unit = group === "linear" ? "ml" : "mq";
      return `${formatDecimal(quantityFromBoxes(count, perBox))} ${unit}`;
    },

    stockTitle(item) {
      const count = this.warehouseCount(item);
      const group = groupOf(item);
      if (group === "linear")
        return itemPerBox(item)
          ? `${count} ${count === 1 ? "confezione" : "confezioni"} in magazzino`
          : "Giacenza non calcolabile: manca ML / confezione nella scheda articolo";
      if (group === "area")
        return itemPerBox(item)
          ? `${count} ${count === 1 ? "scatola" : "scatole"} in magazzino`
          : "Giacenza non calcolabile: manca M² / scatola nella scheda articolo";
      return `${count} pz in magazzino`;
    },

    isQtyExceedingStock(row) {
      const item = this.getItemFromStore(
        row.itemId,
        this.categoryHintForRow(row)
      );
      if (!item?.id) return false;
      const stock = this.warehouseCount(item);
      if (stock <= 0) return false;
      return this.qtyCell(row) > stock;
    },

    /* mq/ml scritti a mano per riga, indicizzati per itemId: il PDF li usa al
       posto della quantità ricavata dalle scatole. */
    mqOverridesForPdf() {
      const map = {};
      (this.formData.items || []).forEach((row) => {
        const mq = Number(row?.mqQuantity);
        if (row?.itemId && mq > 0) map[row.itemId] = mq;
      });
      return map;
    },

    /* mq/ml digitati nel form, se validi (altrimenti null: la riga userà
       l'equivalente delle scatole) */
    typedMqQuantity() {
      if (!this.showMqField) return null;
      const n = Number(this.mqInput);
      return n > 0 ? n : null;
    },

    /* Allinea il campo mq alla quantità già presente in newItem (es. quando si
       riseleziona un articolo che ha già una quantità impostata). */
    syncMqInputFromBoxes() {
      const perBox = perBoxOrFallback(this.mqPerBox);
      const boxes = Number(this.newItem.boxQuantity) || 0;
      this.mqInput = boxes ? quantityFromBoxes(boxes, perBox) : "";
    },

    /* mq/ml per scatola della riga: 0 solo se l'articolo non è venduto a
       scatola, altrimenti il dato dell'articolo (o 1 se manca) */
    rowPerBox(row) {
      const group = this.rowCategoryGroup(row);
      if (group !== "area" && group !== "linear") return 0;
      const it =
        this.getItemFromStore(row.itemId, this.categoryHintForRow(row)) || {};
      const perBox =
        group === "linear"
          ? Number(it.mlPerBox) || 0
          : Number(it.sqmPerBox) || 0;
      return perBoxOrFallback(perBox);
    },

    /* Unità della colonna quantità: mq/ml per gli articoli a scatola, pz per gli
       altri. Le scatole restano la quantità del preventivo ma non si mostrano. */
    rowQtyUnit(row) {
      const group = this.rowCategoryGroup(row);
      if (!this.rowPerBox(row)) return group === "linear" ? "ml" : "pz";
      return group === "linear" ? "ml" : "mq";
    },

    rowQtyStep(row) {
      return this.rowPerBox(row) ? "any" : "1";
    },

    /* Valore in colonna quantità: i mq/ml delle scatole intere, che sono quelli
       che finiscono in fattura. I mq/ml chiesti dall'operatore restano nel
       tooltip e valgono qui solo quando le scatole non sono calcolabili.
       Pezzi per gli articoli non venduti a misura. */
    rowQtyValue(row) {
      const perBox = this.rowPerBox(row);
      if (!perBox) return this.qtyCell(row);
      const fromBoxes = quantityFromBoxes(Number(row.boxQuantity) || 0, perBox);
      if (this.rowHasBoxData(row)) return fromBoxes;
      const typed = Number(row.mqQuantity);
      if (typed > 0) return row.mqQuantity;
      return fromBoxes;
    },

    /* Mq/ml chiesti dall'operatore, quando non coincidono con quelli
       fatturati: l'avanzo della scatola resta al cliente e va detto. */
    rowRequestedNote(row) {
      const perBox = this.rowPerBox(row);
      if (!perBox || !this.rowHasBoxData(row)) return null;
      return requestedQuantityNote({
        requested: row.mqQuantity,
        billed: this.rowQtyValue(row),
        boxes: Number(row.boxQuantity) || 0,
        unit: this.rowCategoryGroup(row) === "linear" ? "ml" : "mq",
      });
    },

    rowQtyTitle(row) {
      const perBox = this.rowPerBox(row);
      if (!perBox) return "Quantità in pezzi";
      // senza mq/ml per scatola le scatole non sono calcolabili: si dice
      // perché, invece di mostrare un numero inventato
      if (!this.rowHasBoxData(row)) {
        return "Scatole non calcolabili: completa la scheda articolo";
      }
      const isLinear = this.rowCategoryGroup(row) === "linear";
      const unit = isLinear ? "ml" : "mq";
      const boxes = Number(row.boxQuantity) || 0;
      const billed = quantityFromBoxes(boxes, perBox);
      const label =
        boxes === 1
          ? isLinear
            ? "confezione"
            : "scatola"
          : isLinear
          ? "confezioni"
          : "scatole";
      const head = `${boxes} ${label} intere (${formatDecimal(
        billed
      )} ${unit})`;
      // La richiesta compare solo se non coincide con quanto si fattura: è
      // l'avanzo che il cliente si porta a casa e paga comunque.
      const requested = Number(row.mqQuantity) || 0;
      if (requested > 0 && Math.abs(requested - billed) >= 0.01) {
        return `${head} per i ${formatDecimal(requested)} ${unit} richiesti`;
      }
      return head;
    },

    updateRowQty(row, val, inputEl = null) {
      const group = this.rowCategoryGroup(row);
      const perBox = this.rowPerBox(row);
      const requested = Number(val) || 0;
      // per togliere l'articolo si usa il pulsante rimuovi, non la quantità 0
      if (requested <= 0) {
        this.$toast.error(
          "La quantità deve essere maggiore di 0: usa il pulsante rimuovi per eliminare l'articolo."
        );
        if (inputEl) inputEl.value = this.rowQtyValue(row);
        return;
      }
      if (perBox) {
        // l'operatore scrive i mq/ml: il valore resta quello digitato, le scatole
        // intere da ordinare sono derivate per il magazzino
        row.mqQuantity = requested;
        const boxes = Math.max(1, boxesFromQuantity(requested, perBox));
        row.boxQuantity = boxes;
        row.quantity = boxes;
        const storeItem = this.getItemFromStore(
          row.itemId,
          this.categoryHintForRow(row)
        );
        row.pieces = boxes * (storeItem?.piecesPerBox || 1);
        return;
      }
      const n = Math.max(1, Math.round(requested));
      if (group === "area" || group === "linear") {
        row.boxQuantity = n;
        const storeItem = this.getItemFromStore(
          row.itemId,
          this.categoryHintForRow(row)
        );
        row.pieces = n * (storeItem?.piecesPerBox || 1);
      } else {
        row.pieces = n;
      }
      // il binding :value non si aggiorna se il valore arrotondato coincide con
      // quello già in stato: riallineo il campo a mano
      if (inputEl && +inputEl.value !== n) inputEl.value = n;
    },

    /* Articolo della riga, risolto dallo store (le righe portano solo l'id) */
    rowStockItem(row) {
      const itemId = row?.itemId ?? row?.id;
      if (row?.id && row?.category) return row;
      return this.getItemFromStore(itemId, this.categoryHintForRow(row));
    },

    /* Riga con scheda articolo incompleta: si evidenzia in tabella, così è
       chiaro cosa completare prima di confermare il preventivo. */
    isRowStockIncomplete(row) {
      return hasIncompleteStockData(this.rowStockItem(row));
    },

    rowStockWarning(row) {
      const item = this.rowStockItem(row);
      const missing = missingStockFields(item);
      if (!missing.length) return "";
      return `Scheda articolo incompleta (manca ${missing.join(
        ", "
      )}): completala prima di confermare il preventivo.`;
    },

    validateQuoteItemsBeforeSubmit() {
      const rows = this.formData.items || [];
      // nessuna riga a quantità 0: il preventivo non deve poter essere salvato
      const zeroQtyRow = rows.find((row) => this.qtyCell(row) < 1);
      if (zeroQtyRow) {
        const label = this.displayForId(
          zeroQtyRow.itemId,
          this.categoryHintForRow(zeroQtyRow)
        ).title;
        this.errorMessage = `Quantità non valida per "${label}": inserisci almeno 1 o rimuovi l'articolo.`;
        this.$toast.error(this.errorMessage);
        return false;
      }
      return true;
    },

    isOrderQueued(item) {
      return !!item?.id && !!this.pendingOrderItems[item.id];
    },

    isOrderQueuedRow(row) {
      return !!row?.itemId && !!this.pendingOrderItems[row.itemId];
    },

    // Quantità da ordinare per una riga (scarto tra richiesta e giacenza)
    /* Quantità mancante espressa nell'unità della riga: per gli articoli a
       mq/ml le scatole da ordinare si traducono nei mq/ml corrispondenti
       (le scatole restano fra parentesi: è quello che si ordina davvero). */
    qtyToOrderLabel(row) {
      const boxes = this.qtyToOrder(row);
      const perBox = this.rowPerBox(row);
      const unit = this.rowQtyUnit(row);
      if (!perBox || unit === "pz") return `${boxes} pz`;
      const qty = formatDecimal(quantityFromBoxes(boxes, perBox));
      const isLinear = unit === "ml";
      const label =
        boxes === 1
          ? isLinear
            ? "confezione"
            : "scatola"
          : isLinear
          ? "confezioni"
          : "scatole";
      return `${qty} ${unit} (${boxes} ${label})`;
    },

    qtyToOrder(row) {
      const item = this.getItemFromStore(
        row.itemId,
        this.categoryHintForRow(row)
      );
      return this.orderQuantityForItem(item);
    },

    orderQuantityForItem(item) {
      if (!item?.id) return 1;

      const stock = this.warehouseCount(item);

      const row = (this.formData.items || []).find(
        (entry) => Number(entry?.itemId) === Number(item.id)
      );
      if (row) {
        const rg = groupOf(item);
        const qty =
          rg === "area" || rg === "linear"
            ? Number(row.boxQuantity || row.quantity || 0)
            : Number(row.pieces || row.quantity || 0);
        if (qty > 0) {
          return stock > 0 ? Math.max(qty - stock, 1) : qty;
        }
      }

      if (this.newItem.itemId === item.id) {
        const g = groupOf(item);
        const qty =
          g === "area" || g === "linear"
            ? Number(this.newItem.boxQuantity || 0)
            : Number(this.newItem.pieces || 0);
        if (qty > 0) return stock > 0 ? Math.max(qty - stock, 1) : qty;
      }
      return 1;
    },

    buildPendingOrderNote(quoteId) {
      return `Ordine per preventivo #${quoteId}`;
    },

    async processPendingOrders(quoteId) {
      const itemIds = Object.keys(this.pendingOrderItems || {}).map(Number);
      if (!quoteId || !itemIds.length) return;

      const results = await Promise.allSettled(
        itemIds.map((itemId) =>
          this.createOrder({
            itemId,
            quantity: this.orderQuantityForItem(this.getItemFromStore(itemId)),
            notes: this.buildPendingOrderNote(quoteId, itemId),
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        this.$toast.error(
          `${failed.length} carico/i merce non creato/i dopo il salvataggio del preventivo.`
        );
      } else {
        this.$toast.success(
          "Carichi merce collegati al preventivo creati con successo."
        );
      }
    },

    queueOrderFromQuoteItem(item) {
      if (!item?.id || this.isOrderQueued(item)) return;

      this.pendingOrderItems = {
        ...this.pendingOrderItems,
        [item.id]: true,
      };

      this.$toast.info(
        "Ordine messo in coda. Verrà creato al salvataggio del preventivo."
      );
    },

    unqueueOrderFromQuoteItem(item) {
      if (!item?.id || !this.isOrderQueued(item)) return;

      const next = { ...this.pendingOrderItems };
      delete next[item.id];
      this.pendingOrderItems = next;

      if (this.newItem.itemId === item.id) this.orderNote = "";

      this.$toast.info("Ordine rimosso dalla coda.");
    },

    selectItem(item) {
      this.newItem.itemId = item.id;
      this.orderNote = "";

      // Prezzo di listino NETTO (IVA esclusa), invariato al cambio IVA.
      const listNet = Number(item.price) || 0;
      this.searchQueryItem = this.itemSearchLabel(item);

      // cache namespaced
      this.selectedByKey[KEY.STOCK(item.id)] = item;

      // unitPrice = prezzo unitario netto
      this.newItem.price = listNet;
      this.newItem.unitPrice = listNet;

      // Prezzo scontato: inizialmente vuoto (l'operatore lo digita se serve).
      this.newItem.discountedPrice = "";

      // il campo mq deve riflettere la quantità dell'articolo appena scelto
      this.syncMqInputFromBoxes();
      this.precompilaTassazioneDaRiga();
      this.hideDropdown();
    },

    selectClient(client) {
      this.formData.clientId = client.id;
      this.searchQueryClient = client.clientName;
      this.hideDropdown();
    },

    startSearch(type) {
      this.activeSearch = type; // 'client' o 'item'
      this.showDropdown = true;
      this.selectedIndex = -1;

      // se parte una nuova ricerca resetta l’oggetto che stavi "editando"
      if (type === "item") {
        this.newItem = this.resetNewItem();
      }
      if (type === "client") this.searchQueryClient = "";
      if (type === "item") this.searchQueryItem = "";
    },

    handleKey(event) {
      if (!this.showDropdown) return;

      // Quale array devo navigare?
      let list;
      if (this.activeSearch === "client") {
        list = this.filteredClients;
      } else if (this.activeSearch === "item") {
        list = this.filteredItems;
      } else {
        list = [];
      }

      // ========== freccia giù ==========
      if (event.key === "ArrowDown") {
        this.selectedIndex =
          this.selectedIndex < list.length - 1 ? this.selectedIndex + 1 : 0;
        event.preventDefault();

        // ========== freccia su ==========
      } else if (event.key === "ArrowUp") {
        this.selectedIndex =
          this.selectedIndex > 0 ? this.selectedIndex - 1 : list.length - 1;
        event.preventDefault();

        // ========== ENTER ==========
      } else if (event.key === "Enter") {
        if (this.selectedIndex >= 0 && this.selectedIndex < list.length) {
          const chosen = list[this.selectedIndex];

          if (this.activeSearch === "client") {
            // selezione cliente
            this.formData.clientId = chosen.id;
            this.searchQueryClient = chosen.clientName;
          } else if (this.activeSearch === "item") {
            // selezione articolo di magazzino
            this.newItem.itemId = chosen.id;
            this.searchQueryItem = chosen.itemName
              ? `${chosen.itemName} ${chosen.measureDesc || ""}`.trim()
              : `${chosen.subBrand || ""} ${chosen.fixtureType || ""} ${
                  chosen.fixturePosition || ""
                }`.trim();

            // cache namespaced
            this.selectedByKey[KEY.STOCK(chosen.id)] = chosen;

            // delega tutta la logica a onSelectItem (prezzo, iva, reset, ecc.)
            this.onSelectItem();
          }
          this.showDropdown = false;
          this.selectedIndex = -1;
        }
        event.preventDefault();
      }
    },

    hideDropdown() {
      // Usa un timeout per permettere il click di selezione sul dropdown prima di nasconderlo
      setTimeout(() => {
        this.showDropdown = false;
        this.selectedIndex = -1;
        this.activeSearch = null; // Aggiungi questa linea per resettare l'indice
      }, 300);
    },

    /* Numero opzionale non negativo: preserva lo zero (riga gratuita). Ritorna
       null per assente/vuoto/non numerico o negativo. */
    optionalNonNegativeNumber(value) {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    },

    /**
     * Gestisce la selezione di un articolo dal dropdown.
     * Chiude il dropdown e aggiorna la searchQuery con il nome dell'articolo selezionato.
     */
    // async onSelectItem() {
    //   this.hideDropdown();

    //   const chosenId = this.newItem.itemId;
    //   const item = this.getItemFromStore(chosenId, "STOCK");
    //   if (!item.id) return;

    //   // reset "pulito", ma con la stessa IVA
    //   const iva = this.newItem.iva || 22;
    //   this.newItem = this.resetNewItem();
    //   this.newItem.itemId = chosenId;
    //   this.newItem.iva = iva;
    //   this.newItem.category = item.category || "";

    //   // disaccoppia l’altra selezione
    //   this.formData.outstockId = null;
    //   this.searchQueryOutstock = "";

    //   this.selectedItemById[item.id] = item;
    //   this.searchQueryItem = item.itemName
    //     ? `${item.itemName} ${item.measureDesc || ""}`.trim()
    //     : `${item.subBrand || ""} ${item.fixtureType || ""} ${
    //         item.fixturePosition || ""
    //       }`.trim();

    //   const prezzoIvato = this.adjustedPrice(item.price, this.newItem.iva);
    //   this.newItem.price = prezzoIvato;
    //   this.newItem.unitPrice = prezzoIvato;
    // },

    async onSelectItem() {
      this.hideDropdown();
      const chosenId = this.newItem.itemId;
      const item = this.getItemFromStore(chosenId, "STOCK");
      if (!item.id) return;

      const iva = this.newItem.iva ?? 22;
      // [sc-49176] Con l'IVA resta la Natura.
      const natura = this.newItem.natura ?? null;
      this.newItem = this.resetNewItem();
      this.newItem.itemId = chosenId;
      Object.assign(this.newItem, { iva, natura });
      this.newItem.category = item.category || "";

      this.selectedByKey[KEY.STOCK(item.id)] = item;
      this.searchQueryItem = item.itemName
        ? `${item.itemName} ${item.measureDesc || ""}`.trim()
        : `${item.subBrand || ""} ${item.fixtureType || ""} ${
            item.fixturePosition || ""
          }`.trim();

      // Prezzo di listino NETTO (IVA esclusa).
      const listNet = Number(item.price) || 0;
      this.newItem.price = listNet;
      this.newItem.unitPrice = listNet;
      this.precompilaTassazioneDaRiga();
    },

    handleClientSelection() {
      this.showDropdown = false;
      this.searchQueryClient = this.getClientName(this.formData.clientId);
    },
    /**
     * Recupera i dati del preventivo da modificare.
     * Viene chiamato appena il componente è montato.
     */
    async fetchQuoteData() {
      try {
        this.errorMessage = "";
        const response = await axiosInstance.get(`/quote/${this.quoteId}`);
        // Adatta il percorso a seconda della tua API
        if (response.status === 200) {
          const quoteData = response.data.quote; // notare che i dati stanno sotto `quote`
          // console.log("fetchQuoteData quoteData", quoteData);
          // console.log("quoteData.response.data.quote", quoteData);
          // items viene dal server con item + item.QuoteItem
          // Creiamo un array "flat" con le proprietà necessarie
          // console.log("quoteData.quote_items", quoteData.quote_items);
          const mappedItems = (quoteData.quote_items || [])
            .map((qi) => {
              if (qi.item) {
                // ───── articolo di magazzino ─────
                const it = qi.item;
                const isBoxBased = isSoldByMeasure(it);

                return {
                  // [sc-49176 / task 11] L'id della riga: serve a «Salva la
                  // Natura» sugli ordini chiusi.
                  quoteItemId: qi.id,
                  itemId: it.id,
                  itemName: it.itemName || it.subBrand,
                  category: it.category,
                  boxQuantity: isBoxBased ? qi.quantity : 0,
                  pieces: isBoxBased ? 0 : qi.quantity,
                  mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
                  // Netto applicato: scontato storico se presente, altrimenti il
                  // prezzo netto della riga (nuova semantica IVA esclusa). Al
                  // salvataggio verrà inviato come price con discountValue null.
                  price:
                    qi.discountedPrice != null
                      ? Number(qi.discountedPrice)
                      : Number(qi.price),
                  discountedPrice: null,
                  iva: qi.iva,
                  natura: qi.natura ?? null,
                  legalReference: qi.legalReference ?? null,
                };
              } else {
                return null;
              }
            })
            .filter(Boolean);

          // ⚠️ subito dopo const mappedItems = ...
          (quoteData.items || []).forEach((it) => {
            // sono articoli di MAGAZZINO
            if (it && it.id != null) {
              this.selectedByKey[KEY.STOCK(it.id)] = it;
            }
          });

          (quoteData.quote_items || []).forEach((qi) => {
            if (qi.item && qi.item.id != null) {
              // stock
              this.selectedByKey[KEY.STOCK(qi.item.id)] = {
                ...qi.item,
                // in certi dump itemName è "", ma brand/subBrand ci sono
                // non forziamo qui: ci pensa nameForDisplay al fallback
              };
            }
          });

          this.formData.items = mappedItems;
          // console.log("this.formData.items", this.formData.items);
          this.formData.date = toDateLocal(quoteData.date);

          // this.formData.date = this.formatToDateTimeLocal(quoteData.date);
          this.formData.clientId = quoteData.clientId || "";
          const c = (this.clients || []).find(
            (x) => x.id === this.formData.clientId
          );
          this.searchQueryClient = c
            ? this.getClientLabel(c)
            : quoteData.clientName || "";
          // this.searchQueryClient = quoteData.clientName || "";
          // this.formData.operatorId = quoteData.operatorId || "";

          // this.formData.eventName = quoteData.eventName || ""; // Se non esiste, assegna una stringa vuota

          this.formData.location = this.normalizeLocFromApi(quoteData.location);

          this.formData.payment_method = quoteData.payment_method;
          this.formData.technical_queries = quoteData.technical_queries || ""; // Se non esiste, assegna una stringa vuota
          this.formData.hasPackagingFee = quoteData.hasPackagingFee ?? false; // addebito spesa imballo
          // Modalità consegna/ritiro: usa il valore salvato; per preventivi
          // vecchi senza il campo, deduci dalla presenza indirizzo.
          this.formData.deliveryType =
            quoteData.deliveryType ||
            (this.formData.location?.address ? "consegna" : "");

          this.offerState = quoteData.offerState ?? null;

          this.modificaChiusa = ordineNonModificabile(quoteData);
          this.natureDaSalvare = {};

          const existingDocs = quoteData.documents || [];
          if (existingDocs.length > 0) {
            this.pdfUploaded = true;
            this.lastUploadedDocName = existingDocs.at(-1).name;
          }

          this.$nextTick(() => {
            this.successMessage = `Dati del preventivo con ID:${this.quoteId} caricati con successo`;
            this.$toast.success(this.successMessage);
            this.captureSnapshot(); // stato iniziale caricato
          });
        }
      } catch (error) {
        // console.error("fetchQuoteData error:", error);
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    toggleItemsList() {
      this.panelVisible = !this.panelVisible;
    },

    // [sc-49176] Un solo percorso per portare i campi in tabella, usato da
    // «Aggiungi» e dal salvataggio: stesso articolo = stessa riga aggiornata
    // (quantità, prezzo, aliquota, Natura), mai una copia.
    inserisciRigaDaCampi() {
      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";

      // Prezzo NETTO applicato: scontato se valorizzato (zero incluso),
      // altrimenti il listino netto. Nessuno sconto inviato al backend.
      const personalized = this.optionalNonNegativeNumber(
        this.newItem.discountedPrice
      );
      const appliedNet =
        personalized !== null
          ? personalized
          : Number(this.newItem.unitPrice) || 0;

      const full = this.getItemFromStore(
        this.newItem.itemId,
        this.categoryHintForRow(this.newItem)
      );
      if (full.id) this.selectedByKey[KEY.STOCK(full.id)] = full;

      // Articolo già in tabella: «Aggiungi» aggiorna la sua riga (quantità,
      // prezzo, aliquota e Natura), non ne crea un'altra.
      const existing = this.formData.items.find(
        (r) => r.itemId === this.newItem.itemId
      );

      if (existing) {
        if (isAreaOrLinear) {
          existing.boxQuantity = this.newItem.boxQuantity;
          existing.pieces = 0;
          existing.mqQuantity = this.typedMqQuantity();
        } else {
          existing.pieces = this.newItem.pieces;
        }
        existing.unitPrice = appliedNet;
        existing.price = appliedNet;
        existing.quantity = existing.pieces || existing.boxQuantity;
        existing.iva = this.newItem.iva;
        existing.natura = this.newItem.natura || null;
        existing.legalReference = riferimentoPredefinito(this.newItem.natura);
        return;
      }

      // nuova riga
      this.formData.items.push({
        itemId: this.newItem.itemId,
        category: this.newItem.category || this.selectedItem.category,
        boxQuantity: isAreaOrLinear ? this.newItem.boxQuantity : 0,
        pieces: isAreaOrLinear ? 0 : this.newItem.pieces,
        // mq/ml scritti dall'operatore: campo di sola UI, non inviato all'API
        mqQuantity: isAreaOrLinear ? this.typedMqQuantity() : null,
        quantity: isAreaOrLinear
          ? this.newItem.boxQuantity
          : this.newItem.pieces,
        // prezzo netto applicato (personalizzato se valorizzato, altrimenti listino)
        unitPrice: appliedNet,
        price: appliedNet,
        iva: this.newItem.iva,
        natura: this.newItem.natura || null,
        // [sc-49176] Il riferimento è sempre quello della Natura.
        legalReference: riferimentoPredefinito(this.newItem.natura),
      });
    },

    // [sc-49176] Prefill di IVA e Natura dalla riga già in tabella: «Aggiungi»
    // per cambiare la sola quantità non riporta la riga al 22%. Prima iva poi
    // natura, nello stesso giro: il watcher di newItem.iva azzera la Natura
    // solo con un'aliquota diversa da zero, e qui una Natura c'è solo a 0%.
    precompilaTassazioneDaRiga() {
      const riga = this.formData.items.find(
        (r) => r.itemId === this.newItem.itemId
      );
      if (!riga) return;
      const iva = riga.iva ?? 22;
      this.newItem.iva = iva;
      this.newItem.natura = Number(iva) === 0 ? riga.natura || null : null;
    },

    addOrUpdateItem() {
      this.errorMessage = "";
      this.successMessage = "";

      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";

      const qty = isAreaOrLinear
        ? Number(this.newItem.boxQuantity || 0)
        : Number(this.newItem.pieces || 0);

      if (!this.newItem.itemId || qty <= 0 || !this.newItem.unitPrice) {
        this.$nextTick(() => {
          this.errorMessage =
            "Compila tutti i campi prima di aggiungere un articolo.";
          this.$toast.error(this.errorMessage);
        });
        return;
      }

      if (!this.naturaPronta) {
        this.errorMessage =
          "Scegli la Natura dell'operazione per una riga senza IVA.";
        this.$toast.error(this.errorMessage);
        return;
      }

      const esisteva = this.formData.items.some(
        (r) => r.itemId === this.newItem.itemId
      );
      this.inserisciRigaDaCampi();
      if (esisteva) {
        this.$nextTick(() => {
          this.successMessage = "Quantità aggiornata per l'articolo esistente.";
          this.$toast.success(this.successMessage);
        });
        return;
      }

      this.$nextTick(() => {
        this.successMessage = "Articolo aggiunto con successo.";
        this.$toast.success(this.successMessage);
      });

      this.newItem = this.resetNewItem();
      this.searchQueryItem = "";
      this.showDropdown = false;
      // this.newItem.itemId = null;
      // this.formData.outstockId = null;
    },

    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    saveToLocalStorage(key, data) {
      localStorage.setItem(key, JSON.stringify(data));
    },

    loadFromLocalStorage(key) {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        return JSON.parse(savedData);
      }
      return null;
    },

    getItemName(id, category) {
      if (!id) return "Sconosciuto";
      const src = this.getItemFromStore(id, category);
      if (src && src.id != null) return this.nameForDisplay(src);

      // 🔚 ultima spiaggia: usa la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        // per sanitari/altro la mappatura include almeno itemName (o subBrand)
        return row.itemName || "Sconosciuto";
      }
      return "Sconosciuto";
    },

    getClientName(clientId) {
      const client = this.clients.find((c) => c.id === clientId);
      return client ? client.clientName : "Sconosciuto";
    },

    getClientLabel(c) {
      if (!c) return "";
      if (c.email) return `${c.clientName} - (${c.email})`;
      if (c.pec) return `${c.clientName} - (${c.pec})`;
      return c.clientName;
    },

    formatPrice(price) {
      // console.log("beforeFormatPrice price", price);
      const currencySymbol = "€"; // Cambia in "$" per il dollaro
      return `${currencySymbol} ${formatDecimal(price)}`;
    },

    unitForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};
      if (
        String(row?.itemName || it?.itemName || "")
          .toLowerCase()
          .includes("battiscopa")
      )
        return "€/mL";
      const g = groupOf(it);
      if (g === "area") return "€/mq";
      if (g === "linear") return "€/ml";
      return "€/pz";
    },

    // mqForRow(row) {
    //   // console.log("row", row);
    //   const item = this.getItemFromStore(
    //     row.itemId,
    //     row.category
    //   );
    //   // console.log("mqForRow item", item);
    //   // solo piastrelle
    //   if (item.category !== "piastrelle" && item.subCategory === "piastrelle") {
    //     return row.pieces;
    //   }
    //   const sqmPerBox = Number(item.sqmPerBox) || 0;
    //   const boxes = Number(row.boxQuantity) || 0;
    //   const total = sqmPerBox * boxes;
    //   return total ? total.toFixed(2) : "";
    // },

    mqForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};

      // Stock piastrelle: scatole × mqPerBox
      if (it.category === "piastrelle") {
        const sqmPerBox = Number(it.sqmPerBox) || 0;
        const boxes = Number(row.boxQuantity) || 0;
        const total = sqmPerBox * boxes;
        return total ? total.toFixed(2) : "N/D";
      }

      // Fuori magazzino piastrelle: i mq coincidono con 'pieces'
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const sqm = Number(row.pieces) || 0;
        return sqm ? sqm.toFixed(2) : "N/D";
      }

      // Altri casi
      return "N/D";
    },

    resetNewItem() {
      return {
        itemId: null,
        boxQuantity: 0,
        pieces: 0,
        price: "",
        discountedPrice: "",
        category: "",
        quantity: 0, // quantità pezzi per articoli fuori magazzino
        iva: 22,
        natura: null,
      };
    },

    /**
     * [sc-48351 / task 7] Risalva la bozza così com'è: niente PDF, niente
     * campi obbligatori, e si resta sulla pagina — chi salva una bozza di
     * solito sta ancora lavorando.
     */
    onSalvaComeBozza() {
      return this.onUpdateQuoteAndGeneratePdf({ comeBozza: true });
    },

    /**
     * «Salva nella bozza ed esci». Se il salvataggio fallisce non si esce:
     * buttare via il lavoro perché il server non ha risposto sarebbe il
     * contrario di quello che il pulsante promette.
     */
    async salvaBozzaEdEsci() {
      await this.onSalvaComeBozza();
      if (this.errorMessage) return;
      this.showBackModal = false;
      this.navigationConfirmed = true;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
        this.pendingNavigation = null;
      } else {
        this.$router.push({ name: this.isOrderRoute ? "ordini" : "quotes" });
      }
    },

    async onUpdateQuoteAndGeneratePdf({ comeBozza = false } = {}) {
      this.errorMessage = "";
      this.successMessage = "";

      // 0) Validazione base. La bozza ne è fuori: articoli e modalità glieli
      //    richiede il passaggio di stato, quando smette di essere una bozza.
      if (!comeBozza && this.formData.items.length === 0) {
        this.$nextTick(() => {
          this.errorMessage = "Aggiungi almeno un articolo prima di salvare";
          this.$toast.error(this.errorMessage);
        });
        return;
      }
      if (!comeBozza && !this.formData.deliveryType) {
        this.$nextTick(() => {
          this.errorMessage =
            "Seleziona la modalità: consegna o ritiro, prima di salvare";
          this.$toast.error(this.errorMessage);
        });
        return;
      }

      this.savingQuote = true;
      try {
        // 1) pulizia item "in input" se presente
        const inSospeso = !!(
          this.newItem.itemId &&
          (this.newItem.boxQuantity ||
            this.newItem.pieces ||
            this.newItem.quantity)
        );

        // [sc-49176] Una riga senza IVA e senza Natura l'API la rifiuta. Il
        // controllo viene prima del push: se fallisce, la tabella e i campi
        // restano come sono, e un altro clic non aggiunge copie della riga.
        if (this.righeSenzaNatura || (inSospeso && !this.naturaPronta)) {
          this.errorMessage =
            "Scegli la Natura delle righe senza IVA prima di salvare.";
          this.$toast.error(this.errorMessage);
          return;
        }

        if (inSospeso) this.inserisciRigaDaCampi();

        // Una bozza può avere righe incomplete: è il suo mestiere.
        if (!comeBozza && !this.validateQuoteItemsBeforeSubmit()) return;

        // 2) costruisci items per API con coercizioni sicure
        const apiItems = this.formData.items.map((row) => {
          const qty = this.cleanNumber(row.boxQuantity || row.pieces, {
            min: 0,
          });
          const mq = Number(row.mqQuantity);
          return {
            itemId: row.itemId,
            quantity: qty,
            // mq/ml digitati: base di calcolo dell'importo lato API
            mqQuantity: mq > 0 ? mq : null,
            price: this.cleanNumber(row.unitPrice ?? row.price, { min: 0 }), // prezzo unitario ivato
            // prezzo finale ivato per riga; discountValue calcolato lato server
            discountedPrice:
              row.discountedPrice != null
                ? this.cleanNumber(row.discountedPrice, { min: 0 })
                : null,
            iva: this.cleanNumber(row.iva ?? 22, { min: 0 }),
            // [sc-49176] Sempre, anche a 0%: una chiave assente l'API la
            // legge come «lascia quella salvata».
            natura: row.natura || null,
            legalReference: row.legalReference || null,
            category: row.category || "",
          };
        });

        // 3) payload base
        let payload = {
          ...this.formData,
          items: apiItems,
        };

        // 4) normalizza data: type="date" dà YYYY-MM-DD, il backend vuole ISO con ora locale
        if (payload.date && payload.date.length === 10) {
          payload.date = new Date(payload.date + "T00:00:00").toISOString();
        }

        // 5) sanitize campi testuali
        payload.payment_method = (payload.payment_method || "").trim();
        payload.technical_queries = this.sanitizeStr(
          payload.technical_queries,
          { maxLen: 8000 }
        );

        // 5) location opzionale + normalizzazione (indirizzo in testo libero)
        if (payload.location) {
          payload.location = this.normalizeLocFromApi(payload.location);
          const L = payload.location || {};
          if (!(L.address || "").trim()) {
            delete payload.location; // se non c'è indirizzo, rimuovi
          }
        }

        // 6) client coerente
        payload.clientId =
          payload.clientId === "" || payload.clientId == null
            ? null
            : Number(payload.clientId);

        // 7) rimuovi duplicati nel payload (difesa ulteriore)
        const keyOf = (r) => `${r.itemId ?? ""}`;
        const seen = new Set();
        payload.items = payload.items.filter((r) => {
          const k = keyOf(r);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });

        // console.log("PAYLOAD:", JSON.stringify(payload, null, 2));

        // => Update preventivo
        const response = await this.updateQuote({
          quoteId: this.quoteId,
          payload,
        });
        this.response = response;
        // console.log("this.response", this.response);

        if (response?.status !== 200 && response?.status !== 201) {
          // ⬇️ ramo 409
          if (
            response?.status === 409 &&
            response?.data.code === "QUOTE_DUPLICATE"
          ) {
            const d = response?.data || {}; // usa quello che hai a disposizione
            const details = (typeof d.details === "object" && d.details) || {};
            const id = details.quoteId ?? d.quoteId ?? null;
            const address =
              details.address ?? this.formData?.location?.address ?? null;
            const msg =
              d.message ||
              d.details ||
              "Esiste già un preventivo per questo cliente allo stesso indirizzo in corso...";

            this.$toast.error(msg);
            this.openDuplicateModal(id, address);
            return;
          }

          // ...altri errori
          this.errorMessage =
            response.data?.details ||
            response.data?.message ||
            "Impossibile aggiornare il preventivo.";
          this.$toast.error(this.errorMessage);
          this.successMessage = "";
          return;
        }

        // 4) Aggiorna stato locale post-update
        const { updatedQuote, totalPrice } = response.data.quote;
        await this.processPendingOrders(
          Number(updatedQuote?.id || this.quoteId)
        );
        this.formData.totalPrice = Number(totalPrice);

        // Rimappa gli items per la tabella
        this.formData.items = (updatedQuote.quote_items || [])
          .map((qi) => {
            if (!qi.item) return null;
            const it = qi.item;
            const isBoxBased = isSoldByMeasure(it);
            return {
              itemId: it.id,
              itemName: it.itemName || it.subBrand,
              category: it.category,
              boxQuantity: isBoxBased ? qi.quantity : 0,
              pieces: isBoxBased ? 0 : qi.quantity,
              mqQuantity: isBoxBased ? mqFromApi(qi.mqQuantity) : null,
              sqmPerBox: groupOf(it) === "area" ? Number(it.sqmPerBox) || 0 : 0,
              price: Number(qi.price),
              discountedPrice:
                qi.discountedPrice != null ? Number(qi.discountedPrice) : null,
              iva: qi.iva,
              natura: qi.natura ?? null,
              legalReference: qi.legalReference ?? null,
            };
          })
          .filter(Boolean);

        // console.log(
        //   "QUOTE_ITEMS_FROM_API:",
        //   response?.data?.quote?.updatedQuote?.quote_items
        // );

        await this.$nextTick();
        this.captureSnapshot(); // stato aggiornato salvato

        this.$nextTick(() => {
          this.successMessage = comeBozza
            ? "Bozza aggiornata."
            : response.data.message || "Preventivo aggiornato con successo!";
          this.$toast.success(this.successMessage);
        });

        // 5) Genera + carica + salva il PDF. La bozza no: un documento da
        //    finire non è un documento da mandare, e il PDF si fa quando è
        //    pronto.
        if (comeBozza) {
          // `savingQuote` lo rimette a posto il `finally`: toccarlo qui
          // vorrebbe dire due punti che decidono la stessa cosa.
          this.newItem = this.resetNewItem();
          return;
        }

        // [sc-48351 / task 7] Via il ramo `import.meta`: è di Vite, e questo
        // progetto lo compila vue-cli (webpack). Non serviva a niente e
        // rendeva la pagina impossibile da caricare in un test, perché fuori
        // da un modulo `import.meta` è un errore di sintassi.
        const isDev =
          process.env.NODE_ENV === "development" ||
          process.env?.VUE_APP_NODE_ENV === "development";
        await generateQuotePdf({
          quote: updatedQuote,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: this.isOrderRoute,
          mqOverrides: this.mqOverridesForPdf(),
          onComplete: () => {
            this.pdfUploaded = true;
            this.lastUploadedDocName = `Preventivo_N_${updatedQuote.id}${
              isDev ? ".dev" : ""
            }.pdf`;
            this.newItem = this.resetNewItem();
            this.orderNote = "";
            this.pendingOrderItems = {};
          },
        });

        // 6) Redirect alla lista (preventivi o ordini) dopo l'aggiornamento
        setTimeout(() => {
          this.navigationConfirmed = true;
          this.$router.push({ name: this.isOrderRoute ? "ordini" : "quotes" });
        }, 4000);
      } catch (error) {
        // console.error("Errore durante l'aggiornamento del preventivo:", error);
        const resp = error?.response;

        // ⬇️ ramo 409
        if (resp?.status === 409 && resp?.data?.code === "QUOTE_DUPLICATE") {
          const d = resp?.data || {}; // usa quello che hai a disposizione
          const details = (typeof d.details === "object" && d.details) || {};
          const id = details.quoteId ?? d.quoteId ?? null;
          const address =
            details.address ?? this.formData?.location?.address ?? null;
          const msg =
            d.message ||
            d.details ||
            "Esiste già un preventivo per questo cliente allo stesso indirizzo in corso...";

          this.$toast.error(msg);
          this.openDuplicateModal(id, address);
          return;
        }

        // ...altri errori
        this.errorMessage =
          resp?.data?.message ||
          resp?.data?.details ||
          resp?.data?.error ||
          "Errore durante l'aggiornamento del preventivo.";
        this.$toast.error(this.errorMessage);
        this.successMessage = "";
      } finally {
        this.savingQuote = false;
      }
    },

    async onSendDocumentEmail() {
      this.sendingEmail = true;
      try {
        await this.sendQuoteDocumentEmail({
          quoteId: this.quoteId,
          objectName: this.lastUploadedDocName,
        });
        this.$toast.success("Email inviata al cliente con successo.");
      } catch (err) {
        const msg =
          err.response?.data?.error || "Errore durante l'invio dell'email.";
        this.$toast.error(msg);
      } finally {
        this.sendingEmail = false;
      }
    },

    resetLocation() {
      this.formData.location = {
        name: "",
        address: "",
        placeId: "",
        latitude: "",
        longitude: "",
      };
    },

    // Prima ricerca (nuova query): pagina 1, svuota subito i risultati e reset
    // su 404/errore. `reqId` progressivo: le risposte obsolete (digitazione
    // rapida o cambio pagina in volo) vengono ignorate.
    fetchItems(q) {
      const query = (q || "").trim();
      const reqId = ++this.itemsRequestId;
      this.itemsSearchQuery = query;
      this.itemsPage = 1;
      this.itemsRequestedPage = null;
      this.itemsTotalPages = 1;
      this.itemsTotalItems = null;
      // I risultati della ricerca precedente non devono restare visibili né
      // selezionabili durante il caricamento della nuova query.
      this.filteredItems = [];
      this.requestItemsPage(query, 1, reqId, true);
    },
    // Cambio pagina (stessa query): imposta subito la pagina richiesta
    // (pending), poi la carica. Un click su un'altra pagina mentre una è in
    // volo la "supera" (nuovo reqId); i doppioni verso la pagina già richiesta o
    // già caricata sono ignorati. I risultati sostituiscono, non concatenano.
    onItemPageChange(page) {
      const target = Number(page);
      if (!Number.isInteger(target)) return;
      if (target < 1 || target > this.itemsTotalPages) return;
      const active =
        this.itemsRequestedPage != null
          ? this.itemsRequestedPage
          : this.itemsPage;
      if (target === active) return; // niente richieste duplicate
      this.itemsRequestedPage = target;
      const reqId = ++this.itemsRequestId;
      this.requestItemsPage(this.itemsSearchQuery, target, reqId, false);
    },
    // GET condivisa. `isFirstSearch` distingue la gestione errori. Una risposta
    // obsoleta (reqId superato) esce subito senza toccare risultati, pagina o
    // stato loading/pending.
    requestItemsPage(query, page, reqId, isFirstSearch) {
      this.itemsLoading = true;
      axiosInstance
        .get("/item/get-all", { params: { page, limit: 10, search: query } })
        .then(({ data }) => {
          if (reqId !== this.itemsRequestId) return; // risposta obsoleta
          this.filteredItems = data.results || [];
          const p = data.pagination || {};
          this.itemsPage = p.currentPage || page;
          this.itemsTotalPages = p.totalPages || 1;
          this.itemsTotalItems = p.totalItems ?? null;
          this.itemsRequestedPage = null; // pagina caricata: pending azzerato
        })
        .catch((err) => {
          if (reqId !== this.itemsRequestId) return; // risposta obsoleta
          const is404 = err?.response?.status === 404;
          if (!is404) console.error(err);
          if (isFirstSearch) {
            // Nessun risultato / errore in prima ricerca: lista vuota, pagina 1.
            this.filteredItems = [];
            this.itemsPage = 1;
            this.itemsTotalPages = 1;
            this.itemsTotalItems = is404 ? 0 : null;
          }
          // Cambio pagina fallito: si mantengono risultati e itemsPage caricati;
          // si azzera solo la pagina richiesta e si riabilitano i controlli.
          this.itemsRequestedPage = null;
        })
        .finally(() => {
          if (reqId === this.itemsRequestId) this.itemsLoading = false;
        });
    },
    // --- pulizia selezione articolo di magazzino ---
    clearItemSelection() {
      const iva = this.newItem.iva ?? 22; // preserva l'IVA scelta (anche 0%)
      // [sc-49176] ...e con lei la Natura.
      const natura = this.newItem.natura ?? null;
      Object.assign(this.newItem, this.resetNewItem(), { iva, natura });
      this.searchQueryItem = "";
      this.orderNote = "";
    },

    // Apertura modale "Crea articolo".
    openCreateItemModal() {
      console.log("openCreateItemModal"); // DEBUG temporaneo
      this.showCreateItemModal = true;
    },

    // Articolo creato dalla modale: lo rende disponibile nella ricerca e lo
    // auto-seleziona riusando la logica esistente selectItem().
    async onItemCreated({ item, itemCode, message }) {
      let createdItem = item;

      // Fallback robusto: se la response non è completa, rileggo l'articolo
      // con lo stesso endpoint/param (search) usato dalla ricerca del preventivo.
      if (!createdItem || createdItem.id == null || createdItem.price == null) {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: { page: 1, limit: 10, search: itemCode },
        });
        const results = data?.results || [];
        createdItem =
          results.find(
            (it) => it.itemCode === itemCode || it.item_code === itemCode
          ) ||
          results[0] ||
          createdItem;
      }

      if (!createdItem?.id) {
        this.$toast.error("Articolo creato ma non recuperabile.");
        return;
      }

      if (!this.filteredItems.some((it) => it.id === createdItem.id)) {
        this.filteredItems = [createdItem, ...this.filteredItems];
      }

      this.selectItem(createdItem);
      this.showCreateItemModal = false;
      this.$toast.success(message || "Articolo creato e selezionato");
    },
  },

  mounted() {
    this.panelVisible = window.innerWidth >= 768;
    this.fetchQuoteData();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  watch: {
    clients(newList) {
      if (this.formData.clientId && !this.searchQueryClient) {
        const c = newList.find((x) => x.id === this.formData.clientId);
        if (c) this.searchQueryClient = this.getClientLabel(c);
      }
    },

    showDuplicateModal(v) {
      if (v && this.lastDuplicateQuoteInfo?.quoteId) this.loadDuplicateQuote();
    },
    lastDuplicateQuoteInfo: {
      deep: true,
      handler(nv) {
        if (this.showDuplicateModal && nv?.quoteId) this.loadDuplicateQuote();
      },
    },

    // 🔎 Articoli di magazzino
    searchQueryItem(newQ, oldQ) {
      if (oldQ && !newQ.trim() && this.newItem.itemId)
        this.clearItemSelection();
      this.debouncedFetchItems(newQ.trim());
    },

    // 🔒 mutua esclusione (come già facevi)
    // Deselezione articolo (addItem, startSearch, reset form): svuota anche il
    // campo mq

    "newItem.itemId"(v) {
      if (!v) this.mqInput = "";
    },

    // "formData.outstockId"(v) {
    //   if (!v) return;
    //   this.newItem.outstockId = v;
    //   this.newItem.itemId = null;
    // },

    // 💶 ricalcolo prezzo al cambio IVA (invariato salvo minimi ritocchi)
    // "newItem.iva"(val) {
    //   if (this.newItem.itemId) {
    //     const base = this.getItemFromStore(this.newItem.itemId).price;
    //     this.newItem.price = this.adjustedPrice(base, val);
    //     this.newItem.unitPrice = this.newItem.price;
    //     return;
    //   }
    //   if (this.newItem.outstockId) {
    //     const outstock = this.getItemFromStore(
    //       this.newItem.outstockId,
    //       "Fuori Magazzino"
    //     );
    //     const base =
    //       outstock.subCategory === "piastrelle"
    //         ? outstock.sqmPrice
    //         : outstock.price;
    //     this.newItem.unitPrice = Number(this.adjustedPrice(base, val));
    //     const qty = this.newItem.pieces || 0;
    //     this.newItem.price = (this.newItem.unitPrice * qty).toFixed(2);
    //   }
    // },

    /* [sc-48351] Quantità tolta: lo sconto che era stato scritto se ne va
       con lei. Restava nel campo spento e sarebbe rientrato in gioco alla
       quantità successiva, senza che nessuno l'avesse riletto. */
    quantitaInserita(ora) {
      if (!ora) this.newItem.discountedPrice = "";
    },

    /* mq/ml digitati -> scatole intere del preventivo (arrotondamento per
       eccesso). Il campo resta com'è stato scritto: nessuna riscrittura.
       Passa da quantityInput per riusare la logica prezzo (Fuori Magazzino). */
    mqInput(v) {
      if (!this.showMqField) return;
      this.quantityInput = boxesFromQuantity(
        v,
        perBoxOrFallback(this.mqPerBox)
      );
    },

    // [sc-49176] Con un'aliquota la Natura non ha senso: si azzera.
    "newItem.iva"(iva) {
      if (Number(iva) !== 0) {
        this.newItem.natura = null;
      }
    },
  },
};
</script>

<style src="@/css/CreateQuotePage.css"></style>
